import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  NumberInput,
  NumberInputField,
  Text,
  Tooltip,
  useBreakpointValue,
  VStack,
} from '@chakra-ui/react';
import styled from '@emotion/styled';
import { ResistomapSwitch } from '@resistapp/client/components/bars/filter-bar/resistomap-switch';
import { MultiOptionConfirmModal } from '@resistapp/client/components/modals/multi-option-confirmation-modal';
import { KEEP_CONFIRMATION_MODAL_OPEN } from '@resistapp/client/components/modals/multi-option-confirmation-modal-utils';
import { MaxWidth, sidePadding } from '@resistapp/client/components/shared/layout';
import { theme } from '@resistapp/client/components/shared/theme';
import { LoadingView } from '@resistapp/client/components/states/loading';
import { useSearchParamsContext } from '@resistapp/client/contexts/search-params-context';
import {
  ServerSheetProvider,
  type SampleSheetSubmissionConfirmationOptions,
} from '@resistapp/client/contexts/server-sheet-context';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { useCountries, useOrgSamplingPointChoices, useUpdateLabWorkbookMetadata } from '@resistapp/client/hooks/api';
import { useNavigateWithQuery } from '@resistapp/client/hooks/use-navigate-with-query';
import { useToastWithoutDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import { useServerSheet } from '@resistapp/client/hooks/use-server-sheet';
import { belongsToAnalysingOrganisation, isProviderAdminUser, isResistomapAdminUser } from '@resistapp/client/utils/access';
import { getVisibleSelectableColumnIds, moveFocusRelative } from '@resistapp/client/utils/ag-grid-sheet-utils';
import { GeneSelectionSheet } from '@resistapp/client/views/gene-selection-view/gene-selection-view';
import { useLabWorkbook } from '@resistapp/client/views/lab-view/api/use-lab-workbook';
import { buildSampleSheetColumnDefs } from '@resistapp/client/views/sample-sheet-view/sample-sheet-columns';
import {
  applySamplingPointToRow,
  buildSamplingPointUniqueKey,
  cloneRow,
  collectRowValidationIssues,
  computeSamplingPointDraftStates,
  getSampleNumberingContinuationMessage,
  hasServerLockedNumbering,
  isFirstSampleNumberEdited,
  isSubmittedSampleLikeRow,
  isRowEmpty,
  isRowReadyForSubmit,
  newSamplingPointSentinelLabel,
  normalizeSamplingPointFields,
  readSheetMetaFirstSampleNumber,
  resolveFirstSampleNumber,
  resolveSampleNumberingContinuationState,
  SampleSheetRowDraft,
  samplingPointFieldKeys,
  samplingPointFieldsEqual,
  SamplingPointSamplingPointData,
  summariseMetadataLimitations,
  type MetadataLimitationCategorySummary,
  type NormalizedSamplingPointFields,
  type RowValidationIssues,
  type SamplingPointDraftState,
} from '@resistapp/client/views/sample-sheet-view/sample-sheet-data';
import {
  ConfirmedSampleNumbering,
  isSheetPatchField,
  PartialVolumeNormalisationConfirmation,
  SampleMetadataLimitationField,
  SampleMetadataLimitationsConfirmation,
  SamplingPointUsage,
  SheetPatchField,
  zSamplingPointUsageResponse,
} from '@resistapp/common/api-types';
import {
  formatSampleTypeLabel,
  formatSamplingPointSubTypeLabel,
  parseNumberCell,
  toSampleType,
  toSamplingPointSubType,
  type SampleSpecRow,
} from '@resistapp/common/sample-sheet';
import {
  labWorkbookServicesAllowMissingPrimaryDnaDetails,
  labWorkbookServicesRequirePrimaryElutedDnaVolume,
} from '@resistapp/common/lab-dna-step';
import {
  hasMixedPrimaryDnaDetailCompletion,
  primaryDnaDetailWorkbookMixedMessage,
  sampleMetadataNumberFieldKeys,
} from '@resistapp/common/sample-sheet-server';
import { normalizeSoftWarnings } from '@resistapp/common/sample-sheet-server/soft-constraints';
import {
  getAbsoluteMinimumDilutedDnaVolume,
  getMinimumDilutedDnaVolume,
} from '@resistapp/common/sample-sheet/diluted-volume-rules';
import { getParentType, isWaterType, SampleType } from '@resistapp/common/sampling-point-types';
import { GenePanelState, getCountryNameByAlpha3, LabWorkbookStatus } from '@resistapp/common/types';
import { useQueryClient } from '@tanstack/react-query';
import {
  AllCommunityModule,
  CellDoubleClickedEvent,
  CellEditingStartedEvent,
  CellEditingStoppedEvent,
  CellKeyDownEvent,
  CellValueChangedEvent,
  FilterChangedEvent,
  ModuleRegistry,
  themeQuartz,
  type CellClickedEvent,
  type CellRange,
  type CellSelectionChangedEvent,
  type CellSelectionOptions,
  type ColDef,
  type DefaultMenuItem,
  type FillEndEvent,
  type FillStartEvent,
  type GetContextMenuItemsParams,
  type GridApi,
  type IHeaderParams,
  type IRowNode,
  type MenuItemDef,
  type ModelUpdatedEvent,
  type PasteEndEvent,
  type PasteStartEvent,
  type RowClassParams,
  type RowSelectionOptions,
} from 'ag-grid-community';
import { ClipboardModule, RichSelectModule } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { cloneDeep, findLast, findLastIndex, isEqual, range as lodashRange } from 'lodash';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MutableRefObject,
  type UIEvent,
} from 'react';
import { useParams } from 'react-router-dom';
import { getAuthHeaders, resistomapApiClient } from '../../utils/fetch';
import { useSmartchipClipboard, type ResolveMode } from './clipboard/use-smartchip-clipboard';
import type { DropdownRowMutation } from './dropdown-field-factory';
import { createCellKey } from './sample-sheet-cell-keys';
import './sample-sheet-grid.css';
import {
  getInitialSampleSheetRowViewMode,
  getSampleSheetRowViewInstanceKey,
  hasSubmittedRowsEligibleForEditing,
  type SampleSheetRowViewMode,
} from './sample-sheet-row-view-mode';
import { SmartChipActionFooter } from './smartchip-action-footer';

ModuleRegistry.registerModules([AllCommunityModule, ClipboardModule, RichSelectModule]);

// DO NOT ADD ag-theme-quartz, it is deprecated
// Also DO NOT import the following: ag-grid-community/styles/ag-grid.css or ag-grid-community/styles/ag-theme-quartz.css
const gridThemeClass = 'sample-sheet-grid';
const ROW_INDEX_COL_ID = '__row_index__';
const SAMPLE_SHEET_EXCLUDED_COL_IDS = [ROW_INDEX_COL_ID] as const;
const TOOLTIP_HIDE_DELAY_MS = 2 ** 31 - 1; // Keep AG Grid tooltips visible until hover ends (~24.9 days)

const AUTO_CREATE_MIN_TRAILING_ROWS = 30;
const AUTO_CREATE_MAX_TRAILING_ROWS = 200;
const AUTO_CREATE_TRAILING_RATIO = 0.25;
const AUTO_CREATE_BATCH_SIZE = 20;
const AUTO_CREATE_MAX_BATCH_SIZE = 500;
const PAGE_SCROLL_ROW_LIMIT = 500;
const GRID_DEFAULT_MIN_HEIGHT = 240;
const hiddenFloatingHorizontalScrollState = {
  visible: false,
  left: 0,
  width: 0,
  contentWidth: 0,
};

const genePanelStateColors: Record<GenePanelState, string> = {
  [GenePanelState.VALID]: 'green',
  [GenePanelState.INVALID]: 'orange',
  [GenePanelState.LOCKED]: 'purple',
};

const GENE_SELECTION_REQUIRED_MESSAGE = 'Please select a valid set of genes to analyse before submitting samples.';

function getGenePanelBadgeColor(state: GenePanelState | null): string {
  if (!state) {
    return 'orange';
  }
  return genePanelStateColors[state];
}

function getGenePanelBadgeLabel(state: GenePanelState | null): string {
  if (!state) {
    return 'Not linked';
  }
  switch (state) {
    case GenePanelState.VALID:
      return 'Valid';
    case GenePanelState.INVALID:
      return 'Invalid';
    case GenePanelState.LOCKED:
      return 'Locked';
    default:
      return state;
  }
}

function useLatestRef<T>(value: T): MutableRefObject<T> {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

function parseSampleNumberFromLabel(label: string | null | undefined): number | null {
  if (typeof label !== 'string') {
    return null;
  }
  const match = label.trim().match(/^(\d+)/);
  if (!match) {
    return null;
  }
  const n = Number(match[1]);
  if (!Number.isFinite(n) || n <= 0) {
    return null;
  }
  return n;
}

function formatSamplingPointUsageSentence(usage: SamplingPointUsage): string {
  const segments = [
    usage.linkedSamplesCount > 0
      ? `${usage.linkedSamplesCount} submitted sample${usage.linkedSamplesCount === 1 ? '' : 's'}`
      : null,
    usage.linkedDraftRowsCount > 0
      ? `${usage.linkedDraftRowsCount} draft row${usage.linkedDraftRowsCount === 1 ? '' : 's'}`
      : null,
  ].filter((segment): segment is string => segment != null);

  if (segments.length === 0) {
    return "This sampling point isn't linked to any submitted samples or draft rows.";
  }

  return `This sampling point is linked to ${segments.join(' and ')}.`;
}

type RangeSelectionSummary = {
  rowCount: number;
  columnCount: number;
  cellCount: number;
};

type RowSummarySegment = {
  key: string;
  label: string;
  onClick?: () => void;
};

type SmartChipActionFooterDensity = 'full' | 'condensed' | 'compact';

// Keeps cell selections tied to their underlying data when read-only rows are toggled on/off.
type SelectionSnapshotRange = {
  rowIds: string[];
  columnIds: string[];
};

type SelectionSnapshot = {
  ranges: SelectionSnapshotRange[];
  anchorRowId: string | null;
  focusedRowId: string | null;
  focusedColumnId: string | null;
};

type SamplingPointOptions = {
  labels: string[];
  labelToSmplPntId: Record<string, number | undefined>;
  envById: Record<number, SamplingPointSamplingPointData | undefined>;
  envKeyById: Record<number, string | undefined>;
  envIdsByKey: Record<string, number[] | undefined>;
  labelBySmplPntId: Record<number, string | undefined>;
  envIdsByName: Record<string, number[] | undefined>;
  draftByKey: Record<string, NormalizedSamplingPointFields | undefined>;
  draftKeyByLabel: Record<string, string | undefined>;
};

type QueuePatchOp = {
  rowId: string;
  field: SheetPatchField;
  value: unknown;
  previousValue?: unknown;
};

type BatchMode = 'none' | 'paste' | 'undo' | 'redo' | 'fill';

type EditHistoryMode = 'edit' | 'paste' | 'fill';

type CellHistoryChange = {
  rowId: string;
  field: keyof SampleSheetRowDraft;
  previousValue: unknown;
  newValue: unknown;
};

type EditAction = {
  mode: EditHistoryMode;
  cells: CellHistoryChange[];
};

type SamplingPointEditBlock = {
  rowId: string;
  samplingPointId: number;
  field: keyof SampleSheetRowDraft;
  usage: SamplingPointUsage;
};

type PendingPartialVolumeConfirmation = {
  source: 'submit' | 'resubmit';
  rowIds: string[];
  payload: PartialVolumeNormalisationConfirmation;
  confirmations: SampleSheetSubmissionConfirmationOptions;
};

type PendingMetadataLimitationsConfirmation = {
  source: 'submit' | 'resubmit';
  rowIds: string[];
  payload: SampleMetadataLimitationsConfirmation;
  confirmations?: SampleSheetSubmissionConfirmationOptions;
};

type SampleSheetSubmissionAttemptOutcome =
  | 'submitted'
  | 'metadata_limitations_confirmation'
  | 'partial_volume_confirmation';

const metadataLimitationFieldLabels: Record<SampleMetadataLimitationField, string> = {
  filteredVolume: 'Filtered volume',
  primElutedDnaVolume: 'Eluted DNA volume',
  primElutedDnaConcentration: 'Eluted DNA concentration',
  primElutedDnaQuality280: 'Eluted DNA quality 260/280',
  primElutedDnaQuality230: 'Eluted DNA quality 260/230',
  primDilutedDnaConcentration: 'Diluted DNA concentration',
  primDilutedDnaQuality280: 'Diluted DNA quality 260/280',
  primDilutedDnaQuality230: 'Diluted DNA quality 260/230',
  primDilutedDnaVolume: 'Diluted DNA volume',
};

const MIXED_FILTERED_VOLUME_MESSAGE =
  'Some water samples in this project have a filtered volume and some do not. Volume-based results are therefore ' +
  'unavailable for every sample in the project, including those that do have one.';

function getMetadataLimitationConsequence(group: MetadataLimitationCategorySummary): string {
  switch (group.category) {
    case 'volume_normalisation':
      // Suspended solids and BOD outputs have no projects yet, so naming them would only confuse customers.
      // Restore ', per mg suspended solids or per mg BOD' after 'nor per hour' once we have that data.
      return (
        'Without these values the results cannot be normalised by volume: they will not be available as ' +
        'copies / L, nor per hour. Relative abundance and copies / 10 µL DNA are not affected.'
      );
    case 'sample_volume':
      return (
        'The laboratory analyses the material that arrives. ' +
        (group.recommendedMinimum != null
          ? `The selected chip layout needs ${group.recommendedMinimum} µL per sample; with less, some `
          : 'If there is less DNA than the selected chip layout needs, some ') +
        'assays may not be covered and the results for these samples may be incomplete or suboptimal.'
      );
    case 'sample_quality':
      return (
        'Without the purity ratios we cannot check the DNA quality before analysis. Impurities can inhibit the ' +
        'qPCR reaction and affect the results for these samples.'
      );
  }
}

// Frozen when the confirmation opens: the submitter agrees to these exact numbers, so this is both what the dialog
// shows and what the server is asked to honour, even if the workbook's numbering moves in between. Only the analysing
// side sees it - customer submitters fill in a workbook that was numbered for them and often do not know a reanalysis
// happened at all, so they keep the plain assurance prompt.
type PendingSubmitConfirmation =
  | ({ kind: 'numbering' } & ConfirmedSampleNumbering)
  | { kind: 'assurance'; count: number };

function formatConfirmedSampleNumbers(confirmed: Pick<ConfirmedSampleNumbering, 'first' | 'last'>): string {
  return confirmed.first === confirmed.last ? `${confirmed.first}` : `${confirmed.first}–${confirmed.last}`;
}

const sampleNumberDisplayFieldKeys: ReadonlyArray<keyof SampleSheetRowDraft> = ['sampleNumber', 'bioReplica'];

type GridContextShape = {
  sampleSheet?: {
    moveSelectionAfterEdit?: (
      columnId: string | null | undefined,
      rowIndex: number | null | undefined,
      delta: number,
    ) => void;
  };
};

type RowIndexHeaderParams = IHeaderParams & {
  onSelectAll?: () => void;
};

function RowIndexHeaderComponent({ onSelectAll }: RowIndexHeaderParams) {
  const handleActivate = () => {
    onSelectAll?.();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleActivate();
    }
  };

  return (
    <div
      className="sample-sheet-row-index-header-button"
      role="button"
      tabIndex={0}
      aria-label="Select all rows"
      title="Select all rows"
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
    >
      <span>TUBE LABEL</span>
    </div>
  );
}

function displayFieldKeys(field: keyof SampleSheetRowDraft): ReadonlyArray<keyof SampleSheetRowDraft> {
  return field === 'sampleNumber' ? sampleNumberDisplayFieldKeys : [field];
}

function clearDraftField<K extends keyof SampleSheetRowDraft>(draft: SampleSheetRowDraft, key: K): boolean {
  if (draft[key] == null) {
    return false;
  }
  draft[key] = null as SampleSheetRowDraft[K];
  return true;
}

function snapshotCellValue(value: unknown): unknown {
  if (value == null) {
    return null;
  }
  if (typeof value === 'object') {
    return cloneDeep(value);
  }
  return value;
}

function arraysAreEqual(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function setsAreEqual<T>(left: ReadonlySet<T>, right: ReadonlySet<T>): boolean {
  if (left.size !== right.size) {
    return false;
  }
  for (const value of left) {
    if (!right.has(value)) {
      return false;
    }
  }
  return true;
}

function mapsAreEqual<K, V>(left: ReadonlyMap<K, V>, right: ReadonlyMap<K, V>): boolean {
  if (left.size !== right.size) {
    return false;
  }
  for (const [key, value] of left) {
    if (!right.has(key)) {
      return false;
    }
    if (right.get(key) !== value) {
      return false;
    }
  }
  return true;
}

//

type SampleSheetViewInnerProps = {
  organisationId: number | undefined;
  organisationIdParam?: string;
  labWorkbookStatus?: LabWorkbookStatus | null;
  labWorkbookId?: string | null;
  allowGeneTemplateEdits?: boolean;
};

export function SampleSheetView() {
  const params = useParams<{ organisationId?: string }>();
  const organisationId = useMemo(() => {
    const value = params.organisationId;
    if (!value) {
      return undefined;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }, [params.organisationId]);

  return (
    <ServerSheetProvider organisationId={organisationId ?? null}>
      <SampleSheetViewInner organisationId={organisationId} organisationIdParam={params.organisationId} />
    </ServerSheetProvider>
  );
}

type SampleSheetViewForOrganisationProps = {
  organisationId: number | null | undefined;
  sampleSheetId?: string | null;
  labWorkbookStatus?: LabWorkbookStatus | null;
  labWorkbookId?: string | null;
  allowGeneTemplateEdits?: boolean;
};

export function SampleSheetViewForOrganisation({
  organisationId,
  sampleSheetId,
  labWorkbookStatus,
  labWorkbookId,
  allowGeneTemplateEdits,
}: SampleSheetViewForOrganisationProps) {
  const normalized = typeof organisationId === 'number' && Number.isFinite(organisationId) ? organisationId : undefined;
  const normalizedSheetId = typeof sampleSheetId === 'string' && sampleSheetId.length > 0 ? sampleSheetId : null;
  const rowViewInstanceKey = getSampleSheetRowViewInstanceKey(labWorkbookId, labWorkbookStatus);

  return (
    <ServerSheetProvider key={rowViewInstanceKey} organisationId={normalized ?? null} sampleSheetId={normalizedSheetId}>
      <SampleSheetViewInner
        organisationId={normalized}
        labWorkbookStatus={labWorkbookStatus}
        labWorkbookId={labWorkbookId}
        allowGeneTemplateEdits={allowGeneTemplateEdits}
      />
    </ServerSheetProvider>
  );
}

function SampleSheetViewInner({
  organisationId,
  organisationIdParam,
  labWorkbookStatus: labWorkbookStatusProp,
  labWorkbookId: labWorkbookIdProp,
  allowGeneTemplateEdits = true,
}: SampleSheetViewInnerProps) {
  const { searchParams, setSearchParamsStable } = useSearchParamsContext();
  const navigate = useNavigateWithQuery();
  const { user, isAdmin: isResistomapAdmin } = useUser();
  const canEditSubmittedSamples = isResistomapAdminUser(user) || isProviderAdminUser(user);
  const confirmsSampleNumbering = belongsToAnalysingOrganisation(user);
  const modeParam = searchParams.get('mode');
  const mode: 'samples' | 'genes' = modeParam === 'genes' ? 'genes' : 'samples';
  const resolveSamplingPointRef = useRef<(row: SampleSheetRowDraft, mode: ResolveMode) => SampleSheetRowDraft>(
    row => row,
  );
  const isPastingRef = useRef(false);
  const gridRef = useRef<AgGridReact<SampleSheetRowDraft>>(null);
  const requestMoveSelectionAfterEdit = useCallback(
    (columnId: string | null | undefined, rowIndex: number | null | undefined, delta: number) => {
      const api = gridRef.current?.api ?? null;
      moveFocusRelative(api, columnId ?? null, rowIndex, delta, 0, {
        excludedColIds: SAMPLE_SHEET_EXCLUDED_COL_IDS,
      });
    },
    [],
  );
  const autoCreateInFlightRef = useRef(false);
  const autoCreateErrorNotifiedRef = useRef(false);
  const showToast = useToastWithoutDebounce();
  const {
    ready,
    sheetId,
    rows,
    queuePatch,
    createRows,
    submit,
    beginSampleEdits,
    cancelSampleEdits,
    resubmitSampleEdits,
    pendingCells,
    correctedCells,
    connection,
    lastSubmission,
    genePanelId,
    meta: sheetMeta,
    labWorkbookId: contextLabWorkbookId,
  } = useServerSheet();
  const resolvedLabWorkbookId = contextLabWorkbookId ?? labWorkbookIdProp ?? null;
  const queryClient = useQueryClient();
  const { data: labWorkbookData } = useLabWorkbook(resolvedLabWorkbookId);
  const updateLabWorkbookMetadata = useUpdateLabWorkbookMetadata();
  const labWorkbookStatus =
    labWorkbookStatusProp !== undefined ? labWorkbookStatusProp : labWorkbookData?.workbook.status ?? null;
  const labWorkbookLabSheetUrl = labWorkbookData?.workbook.labSheetUrl ?? null;
  const configuredFirstSampleNumber = resolveFirstSampleNumber({
    workbookFirstSampleNumber: labWorkbookData?.workbook.firstSampleNumber ?? null,
    sheetMeta,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLinkingGenePanel, setIsLinkingGenePanel] = useState(false);
  const [firstSampleNumberInput, setFirstSampleNumberInput] = useState<string>(String(configuredFirstSampleNumber));
  const [pendingSubmitConfirmation, setPendingSubmitConfirmation] = useState<PendingSubmitConfirmation | null>(
    null,
  );
  const [adminRowViewMode, setAdminRowViewMode] = useState<SampleSheetRowViewMode>(() =>
    getInitialSampleSheetRowViewMode(labWorkbookStatus),
  );
  const [isEnteringSubmittedEditMode, setIsEnteringSubmittedEditMode] = useState(false);
  const [isCancellingSubmittedEdits, setIsCancellingSubmittedEdits] = useState(false);
  const [isResubmittingSubmittedEdits, setIsResubmittingSubmittedEdits] = useState(false);
  const [blockedSamplingPointEdit, setBlockedSamplingPointEdit] = useState<SamplingPointEditBlock | null>(null);
  const [pendingPartialVolumeConfirmation, setPendingPartialVolumeConfirmation] =
    useState<PendingPartialVolumeConfirmation | null>(null);
  const [pendingMetadataLimitationsConfirmation, setPendingMetadataLimitationsConfirmation] =
    useState<PendingMetadataLimitationsConfirmation | null>(null);
  const [metadataLimitationsAcknowledged, setMetadataLimitationsAcknowledged] = useState(false);
  const metadataLimitationGroups = useMemo(
    () => summariseMetadataLimitations(pendingMetadataLimitationsConfirmation?.payload.rows ?? []),
    [pendingMetadataLimitationsConfirmation],
  );
  const rowsById = useMemo(() => new Map(rows.map(row => [row.rowId, row])), [rows]);
  const rowsByIdRef = useRef(new Map<string, SampleSheetRowDraft>());
  const preEditRowsRef = useRef(new Map<string, SampleSheetRowDraft>());
  const activeEditingRowIdRef = useRef<string | null>(null);
  useEffect(() => {
    rowsByIdRef.current = new Map(rows.map(row => [row.rowId, cloneRow(row)] as const));
  }, [rows]);
  useEffect(() => {
    setFirstSampleNumberInput(String(configuredFirstSampleNumber));
  }, [configuredFirstSampleNumber]);
  // The workbook summary is the numbering seed, but it is only refetched for the administrator who saved it, and a
  // metadata delta published while the socket was down is never replayed. Refetch it whenever the live sheet
  // announces a different seed, and after every reconnect, so the confirmed range is not previewed from a stale one.
  const sheetMetaFirstSampleNumber = readSheetMetaFirstSampleNumber(sheetMeta);
  const workbookFirstSampleNumber = labWorkbookData?.workbook.firstSampleNumber ?? null;
  const connectionState = connection.state;
  const previousConnectionStateRef = useRef(connectionState);
  const refetchedSeedRef = useRef<number | null>(null);
  useEffect(() => {
    const reconnected = connectionState === 'connected' && previousConnectionStateRef.current !== 'connected';
    previousConnectionStateRef.current = connectionState;
    if (resolvedLabWorkbookId == null) {
      return;
    }
    const announcesDifferentSeed =
      sheetMetaFirstSampleNumber != null &&
      sheetMetaFirstSampleNumber !== workbookFirstSampleNumber &&
      refetchedSeedRef.current !== sheetMetaFirstSampleNumber;
    if (!reconnected && !announcesDifferentSeed) {
      return;
    }
    // Only remember a seed that actually arrived: marking it up front would suppress every later attempt if this
    // refetch failed, stranding the sheet on the stale numbering.
    void queryClient
      .refetchQueries({ queryKey: ['labWorkbook', resolvedLabWorkbookId], exact: true }, { throwOnError: true })
      .then(() => {
        refetchedSeedRef.current = sheetMetaFirstSampleNumber;
      })
      .catch(() => undefined);
  }, [connectionState, queryClient, resolvedLabWorkbookId, sheetMetaFirstSampleNumber, workbookFirstSampleNumber]);
  // Local edit history for manual undo fallback when AG Grid’s internal stack is empty
  const editHistoryRef = useRef<EditAction[]>([]);
  const redoHistoryRef = useRef<EditAction[]>([]);
  const isPerformingManualUndoRef = useRef(false);
  const isPerformingManualRedoRef = useRef(false);
  const pendingHistoryChangesRef = useRef<CellHistoryChange[]>([]);
  const pendingHistoryIndexRef = useRef(new Map<string, number>());
  const linkedHistoryChangesRef = useRef<CellHistoryChange[]>([]);
  const suppressRowMutationHistoryRef = useRef(false);
  const ignoredHistoryCellsRef = useRef<Set<string>>(new Set());
  const samplingPointUsageByIdRef = useRef(new Map<number, SamplingPointUsage>());
  const samplingPointUsageInFlightRef = useRef(new Map<number, Promise<SamplingPointUsage | null>>());
  const [samplingPointUsageCacheVersion, setSamplingPointUsageCacheVersion] = useState(0);
  const pendingSamplingPointEditTargetRef = useRef<{ rowId: string; colId: string; eventKey?: string } | null>(null);

  const isBatchingEditsRef = useRef(false);
  const batchModeRef = useRef<BatchMode>('none');
  const accumulatedOpsRef = useRef<QueuePatchOp[]>([]);
  const accumulatedOpIndexRef = useRef(new Map<string, number>());
  const clearPendingHistory = useCallback(() => {
    pendingHistoryChangesRef.current = [];
    pendingHistoryIndexRef.current = new Map();
  }, []);
  const pushHistoryAction = useCallback((action: EditAction) => {
    if (action.cells.length === 0) {
      return;
    }
    editHistoryRef.current.push(action);
    redoHistoryRef.current = [];
  }, []);

  const recordExplicitHistoryAction = useCallback(
    (historyMode: EditHistoryMode, cells: CellHistoryChange[]) => {
      if (cells.length === 0) {
        return;
      }
      if (isPerformingManualUndoRef.current || isPerformingManualRedoRef.current) {
        return;
      }
      clearPendingHistory();
      pushHistoryAction({
        mode: historyMode,
        cells: cells.map(change => ({
          ...change,
        })),
      });
    },
    [clearPendingHistory, pushHistoryAction],
  );
  const appendPendingHistoryChange = useCallback((change: CellHistoryChange) => {
    const key = `${change.rowId}::${String(change.field)}`;
    const existingIndex = pendingHistoryIndexRef.current.get(key);
    if (typeof existingIndex === 'number') {
      const existing = pendingHistoryChangesRef.current[existingIndex];
      pendingHistoryChangesRef.current[existingIndex] = {
        ...existing,
        newValue: change.newValue,
      };
      return;
    }
    pendingHistoryIndexRef.current.set(key, pendingHistoryChangesRef.current.length);
    pendingHistoryChangesRef.current.push(change);
  }, []);
  const finalizeHistoryBatch = useCallback(
    (batchMode: BatchMode) => {
      if (batchMode !== 'paste' && batchMode !== 'fill') {
        clearPendingHistory();
        return;
      }
      if (pendingHistoryChangesRef.current.length === 0) {
        clearPendingHistory();
        return;
      }
      const actionMode: EditHistoryMode = batchMode === 'paste' ? 'paste' : 'fill';
      pushHistoryAction({
        mode: actionMode,
        cells: pendingHistoryChangesRef.current.map(change => ({
          ...change,
        })),
      });
      clearPendingHistory();
    },
    [clearPendingHistory, pushHistoryAction],
  );
  const recordHistoryChange = useCallback(
    (change: CellHistoryChange) => {
      if (isPerformingManualUndoRef.current || isPerformingManualRedoRef.current) {
        return;
      }
      const activeMode = batchModeRef.current;
      if (activeMode === 'undo' || activeMode === 'redo') {
        return;
      }
      if (isBatchingEditsRef.current && (activeMode === 'paste' || activeMode === 'fill')) {
        appendPendingHistoryChange(change);
        return;
      }
      clearPendingHistory();
      const linkedChanges = linkedHistoryChangesRef.current;
      linkedHistoryChangesRef.current = [];
      const combinedCells = linkedChanges.length > 0 ? [...linkedChanges, change] : [change];
      pushHistoryAction({ mode: 'edit', cells: combinedCells });
    },
    [appendPendingHistoryChange, clearPendingHistory, pushHistoryAction],
  );

  const recordExternalRowMutations = useCallback(
    (row: SampleSheetRowDraft, changes: DropdownRowMutation[]) => {
      if (changes.length === 0) {
        return;
      }
      if (suppressRowMutationHistoryRef.current) {
        return;
      }
      if (isPerformingManualUndoRef.current || isPerformingManualRedoRef.current) {
        return;
      }
      const normalizedChanges = changes.map<CellHistoryChange>(change => ({
        rowId: row.rowId,
        field: change.field,
        previousValue: snapshotCellValue(change.previousValue ?? null),
        newValue: snapshotCellValue(change.newValue ?? null),
      }));
      const activeMode = batchModeRef.current;
      if (activeMode === 'undo' || activeMode === 'redo') {
        return;
      }
      if (isBatchingEditsRef.current && (activeMode === 'paste' || activeMode === 'fill')) {
        normalizedChanges.forEach(appendPendingHistoryChange);
        return;
      }
      if (activeEditingRowIdRef.current === row.rowId) {
        linkedHistoryChangesRef.current = linkedHistoryChangesRef.current.concat(normalizedChanges);
        return;
      }
      clearPendingHistory();
      pushHistoryAction({ mode: 'edit', cells: normalizedChanges });
    },
    [appendPendingHistoryChange, clearPendingHistory, pushHistoryAction],
  );

  const updateSamplingPointUsageCache = useCallback((usageRows: SamplingPointUsage[]) => {
    if (usageRows.length === 0) {
      return;
    }
    const cache = samplingPointUsageByIdRef.current;
    const hasUpdates = usageRows.some(row => {
      const previous = cache.get(row.samplingPointId) ?? null;
      if (previous && isEqual(previous, row)) {
        return false;
      }
      cache.set(row.samplingPointId, row);
      return true;
    });
    if (hasUpdates) {
      setSamplingPointUsageCacheVersion(version => version + 1);
    }
  }, []);

  const fetchSamplingPointUsage = useCallback(
    async (samplingPointIds: number[]): Promise<SamplingPointUsage[]> => {
      if (!sheetId) {
        return [];
      }

      const uniqueIds = Array.from(
        new Set(samplingPointIds.filter((id): id is number => typeof id === 'number' && Number.isFinite(id) && id > 0)),
      );
      if (uniqueIds.length === 0) {
        return [];
      }

      const response = await resistomapApiClient.post(
        `/admin/sample-sheets/${sheetId}/sampling-point-usage`,
        { samplingPointIds: uniqueIds },
        { headers: getAuthHeaders('application/json') },
      );
      const parsed = zSamplingPointUsageResponse.parse(response.data);
      updateSamplingPointUsageCache(parsed.samplingPointUsage);
      return parsed.samplingPointUsage;
    },
    [sheetId, updateSamplingPointUsageCache],
  );

  const ensureSamplingPointUsage = useCallback(
    async (samplingPointId: number): Promise<SamplingPointUsage | null> => {
      if (!Number.isFinite(samplingPointId) || samplingPointId <= 0) {
        return null;
      }
      const cached = samplingPointUsageByIdRef.current.get(samplingPointId) ?? null;
      if (cached) {
        return cached;
      }

      const inFlight = samplingPointUsageInFlightRef.current.get(samplingPointId) ?? null;
      if (inFlight) {
        return await inFlight;
      }

      const promise = fetchSamplingPointUsage([samplingPointId])
        .then(usageRows => usageRows.find(row => row.samplingPointId === samplingPointId) ?? null)
        .finally(() => {
          samplingPointUsageInFlightRef.current.delete(samplingPointId);
        });
      samplingPointUsageInFlightRef.current.set(samplingPointId, promise);
      return await promise;
    },
    [fetchSamplingPointUsage],
  );

  const flushAccumulatedOps = useCallback(() => {
    if (accumulatedOpsRef.current.length === 0) {
      return;
    }
    const opsToFlush = accumulatedOpsRef.current.slice();
    accumulatedOpsRef.current = [];
    accumulatedOpIndexRef.current = new Map();
    queuePatch(opsToFlush);
  }, [queuePatch]);

  const enqueuePatchOps = useCallback(
    (ops: QueuePatchOp[]) => {
      if (ops.length === 0) {
        return;
      }
      if (!isBatchingEditsRef.current) {
        queuePatch(ops);
        return;
      }

      ops.forEach(op => {
        const opKey = `${op.rowId}::${op.field}`;
        const existingIndex = accumulatedOpIndexRef.current.get(opKey);
        if (typeof existingIndex === 'number') {
          const existingOp = accumulatedOpsRef.current[existingIndex];
          const preservedPrevious = existingOp.previousValue;
          accumulatedOpsRef.current[existingIndex] = {
            ...op,
            previousValue: preservedPrevious,
          };
        } else {
          accumulatedOpIndexRef.current.set(opKey, accumulatedOpsRef.current.length);
          accumulatedOpsRef.current.push({ ...op });
        }
      });
    },
    [queuePatch],
  );

  const clearSubmittedSamplingPointForRow = useCallback(
    (rowId: string): boolean => {
      if (!canEditSubmittedSamples) {
        return false;
      }
      const api = gridRef.current?.api ?? null;
      if (!api) {
        return false;
      }
      const node = api.getRowNode(rowId);
      const row = node?.data ?? null;
      if (!node || !row || !isSubmittedSampleLikeRow(row) || row.isReadOnly) {
        return false;
      }

      const fieldsToClear: Array<keyof SampleSheetRowDraft> = ['samplingPointId', ...samplingPointFieldKeys];
      const historyChanges: CellHistoryChange[] = [];
      const ops: QueuePatchOp[] = [];

      fieldsToClear.forEach(field => {
        if (!isSheetPatchField(field)) {
          return;
        }
        const previousValue = row[field] ?? null;
        if (isEqual(previousValue, null)) {
          return;
        }
        historyChanges.push({
          rowId,
          field,
          previousValue: snapshotCellValue(previousValue),
          newValue: null,
        });
        ops.push({
          rowId,
          field,
          value: null,
          previousValue,
        });
        ignoredHistoryCellsRef.current.add(createCellKey(rowId, String(field)));
        node.setDataValue(String(field), null);
      });

      if (historyChanges.length > 0) {
        recordExplicitHistoryAction('edit', historyChanges);
      }
      if (ops.length > 0) {
        enqueuePatchOps(ops);
        api.refreshCells({ rowNodes: [node], force: true });
      }
      return ops.length > 0;
    },
    [canEditSubmittedSamples, enqueuePatchOps, recordExplicitHistoryAction],
  );

  const startBatch = useCallback(
    (batchMode: BatchMode) => {
      if (isBatchingEditsRef.current) {
        if (batchModeRef.current === batchMode) {
          return;
        }
        finalizeHistoryBatch(batchModeRef.current);
        flushAccumulatedOps();
      }
      isBatchingEditsRef.current = true;
      batchModeRef.current = batchMode;
    },
    [finalizeHistoryBatch, flushAccumulatedOps],
  );

  const endBatch = useCallback(() => {
    if (!isBatchingEditsRef.current) {
      return;
    }
    const activeMode = batchModeRef.current;
    flushAccumulatedOps();
    isBatchingEditsRef.current = false;
    batchModeRef.current = 'none';
    finalizeHistoryBatch(activeMode);
  }, [finalizeHistoryBatch, flushAccumulatedOps]);

  useEffect(() => {
    return () => {
      finalizeHistoryBatch(batchModeRef.current);
      flushAccumulatedOps();
      isBatchingEditsRef.current = false;
      batchModeRef.current = 'none';
    };
  }, [finalizeHistoryBatch, flushAccumulatedOps]);

  const { data: countriesByAlpha3 } = useCountries();
  const countryNameByAlpha3 = useMemo(
    () => (countriesByAlpha3 ? getCountryNameByAlpha3(countriesByAlpha3) : {}),
    [countriesByAlpha3],
  );

  const samplingPointDraftStatesByRowId = useMemo(() => computeSamplingPointDraftStates(rows), [rows]);
  const getSamplingPointDraftState = useCallback(
    (rowId: string): SamplingPointDraftState | null => samplingPointDraftStatesByRowId.get(rowId) ?? null,
    [samplingPointDraftStatesByRowId],
  );
  const getSamplingPointDraftStateRef = useLatestRef(getSamplingPointDraftState);

  const samplingPointKeyDetails = useMemo(() => {
    const keyByRowId = new Map<string, string | null>();
    const countByKey = new Map<string, number>();
    rows.forEach(row => {
      const key = buildSamplingPointUniqueKey(row);
      keyByRowId.set(row.rowId, key);
      if (!key) {
        return;
      }
      countByKey.set(key, (countByKey.get(key) ?? 0) + 1);
    });
    return {
      keyByRowId,
      countByKey,
    } as const;
  }, [rows]);

  const [samplingPointHighlightByRowId, setSamplingPointHighlightByRowId] = useState<Map<string, 'existing' | 'new'>>(
    new Map(),
  );
  const samplingPointHighlightByRowIdRef = useLatestRef(samplingPointHighlightByRowId);
  const previousSamplingPointHighlightRowIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const api = gridRef.current?.api;
    const previousHighlightRowIds = previousSamplingPointHighlightRowIdsRef.current;
    const rowsToRefresh = new Set<string>(previousHighlightRowIds);
    samplingPointHighlightByRowId.forEach((_state, rowId) => {
      rowsToRefresh.add(rowId);
    });

    if (!api) {
      previousSamplingPointHighlightRowIdsRef.current = new Set(samplingPointHighlightByRowId.keys());
      return;
    }

    if (rowsToRefresh.size > 0) {
      const rowNodes = Array.from(rowsToRefresh)
        .map(rowId => api.getRowNode(rowId))
        .filter((node): node is IRowNode<SampleSheetRowDraft> => node != null);
      if (rowNodes.length > 0) {
        api.refreshCells({
          rowNodes,
          columns: samplingPointFieldKeys as string[],
          force: true,
          suppressFlash: true,
        });
      }
    }

    previousSamplingPointHighlightRowIdsRef.current = new Set(samplingPointHighlightByRowId.keys());
  }, [samplingPointHighlightByRowId]);

  const maybeEnsureRowCapacity = useCallback(
    (_reason: 'effect' | 'scroll' | 'paste') => {
      if (!ready || sheetId == null) {
        return;
      }
      if (connection.state !== 'connected') {
        return;
      }
      if (adminRowViewMode === 'submitted') {
        return;
      }
      if (autoCreateInFlightRef.current) {
        return;
      }

      const editableRows = rows.filter(row => !row.isReadOnly && !isSubmittedSampleLikeRow(row));
      const sortedEditableRows = editableRows.slice().sort((left, right) => {
        const leftIndex = typeof left.rowIndex === 'number' ? left.rowIndex : Number.MAX_SAFE_INTEGER;
        const rightIndex = typeof right.rowIndex === 'number' ? right.rowIndex : Number.MAX_SAFE_INTEGER;
        if (leftIndex !== rightIndex) {
          return leftIndex - rightIndex;
        }
        return left.rowId.localeCompare(right.rowId);
      });

      const lastNonEmptyIndex = findLastIndex(sortedEditableRows, (row: SampleSheetRowDraft) => !isRowEmpty(row));
      const trailingEmpty =
        lastNonEmptyIndex === -1 ? sortedEditableRows.length : sortedEditableRows.length - lastNonEmptyIndex - 1;

      const usedCount = sortedEditableRows.length - trailingEmpty;
      const dynamicTarget = Math.ceil(usedCount * AUTO_CREATE_TRAILING_RATIO);
      const desiredTrailing = Math.min(
        AUTO_CREATE_MAX_TRAILING_ROWS,
        Math.max(AUTO_CREATE_MIN_TRAILING_ROWS, dynamicTarget),
      );

      const missing = desiredTrailing - trailingEmpty;
      if (missing <= 0) {
        autoCreateErrorNotifiedRef.current = false;
        return;
      }

      const createCount = Math.min(AUTO_CREATE_MAX_BATCH_SIZE, Math.max(AUTO_CREATE_BATCH_SIZE, missing));
      if (createCount <= 0) {
        return;
      }

      autoCreateInFlightRef.current = true;
      void createRows(createCount)
        .then(() => {
          autoCreateErrorNotifiedRef.current = false;
        })
        .catch(error => {
          if (!autoCreateErrorNotifiedRef.current) {
            autoCreateErrorNotifiedRef.current = true;
            const description = error instanceof Error ? error.message : 'Unknown error';
            showToast({ status: 'error', title: 'Failed to add rows', description });
          }
        })
        .finally(() => {
          autoCreateInFlightRef.current = false;
        });
    },
    [adminRowViewMode, connection.state, createRows, ready, rows, sheetId, showToast],
  );

  useEffect(() => {
    maybeEnsureRowCapacity('effect');
  }, [maybeEnsureRowCapacity]);

  const refreshLabWorkbookDependentQueries = useCallback(
    (labWorkbookId: string) => {
      void queryClient.refetchQueries({ queryKey: ['labWorkbook', labWorkbookId], exact: true }).catch(() => undefined);
      void queryClient
        .invalidateQueries({ queryKey: ['labDnaStep', labWorkbookId], exact: true })
        .catch(() => undefined);
    },
    [queryClient],
  );

  const lastSubmissionRef = useRef<number | null>(null);
  useEffect(() => {
    if (!lastSubmission) {
      return;
    }
    if (lastSubmissionRef.current === lastSubmission.at) {
      return;
    }
    lastSubmissionRef.current = lastSubmission.at;
    showToast({
      status: 'success',
      title: 'Submission complete',
      description: `${lastSubmission.submitted} row${lastSubmission.submitted === 1 ? '' : 's'} submitted, ${lastSubmission.locked} locked.`,
    });
    if (resolvedLabWorkbookId) {
      refreshLabWorkbookDependentQueries(resolvedLabWorkbookId);
    }
  }, [lastSubmission, refreshLabWorkbookDependentQueries, resolvedLabWorkbookId, showToast]);

  const manualResolveMode: ResolveMode = useMemo(() => ({ kind: 'manualOrSingleField', allowByKey: true }), []);
  const multiPasteResolveMode: ResolveMode = useMemo(() => ({ kind: 'multiCellPaste', allowByKey: false }), []);

  type QueueRowUpdatesOptions = {
    onOps?: (ops: QueuePatchOp[]) => void;
  };

  const queueRowUpdates = useCallback(
    (row: SampleSheetRowDraft, mutatedFields: Set<keyof SampleSheetRowDraft>, options?: QueueRowUpdatesOptions) => {
      if (isPerformingManualUndoRef.current || isPerformingManualRedoRef.current) {
        return;
      }
      const previous =
        preEditRowsRef.current.get(row.rowId) ?? rowsByIdRef.current.get(row.rowId) ?? rowsById.get(row.rowId);
      if (!previous || previous.isReadOnly) {
        return;
      }

      const isSubmittedSampleRow = isSubmittedSampleLikeRow(row);
      const currentSamplingPointId =
        typeof row.samplingPointId === 'number' && Number.isFinite(row.samplingPointId) ? row.samplingPointId : null;
      const previousSamplingPointId =
        typeof previous.samplingPointId === 'number' && Number.isFinite(previous.samplingPointId)
          ? previous.samplingPointId
          : null;
      const isInPlaceSubmittedSamplingPointEdit =
        isSubmittedSampleRow &&
        currentSamplingPointId != null &&
        previousSamplingPointId != null &&
        currentSamplingPointId === previousSamplingPointId;
      const hasSamplingPointMutations =
        mutatedFields.has('samplingPointId') || samplingPointFieldKeys.some(field => mutatedFields.has(field));
      const baseRow = cloneRow(row);
      const resolveMode = mutatedFields.size <= 1 ? manualResolveMode : multiPasteResolveMode;
      const resolvedRow =
        mutatedFields.size > 0 &&
        (!isSubmittedSampleRow || (hasSamplingPointMutations && !isInPlaceSubmittedSamplingPointEdit))
          ? resolveSamplingPointRef.current(baseRow, resolveMode)
          : baseRow;
      const candidateFields = new Set<keyof SampleSheetRowDraft>(mutatedFields);

      if (mutatedFields.size > 0 && !isSubmittedSampleRow) {
        candidateFields.add('samplingPointId');
        candidateFields.add('sampleDescription');
        candidateFields.add('sampleType');
        candidateFields.add('detailedSampleType');
        candidateFields.add('country');
        candidateFields.add('stateOrRegion');
        candidateFields.add('city');
        candidateFields.add('latitudeLongitude');
      }

      if (
        mutatedFields.size > 0 &&
        isSubmittedSampleRow &&
        hasSamplingPointMutations &&
        !isInPlaceSubmittedSamplingPointEdit
      ) {
        candidateFields.add('samplingPointId');
        candidateFields.add('sampleDescription');
        candidateFields.add('sampleType');
        candidateFields.add('detailedSampleType');
        candidateFields.add('country');
        candidateFields.add('stateOrRegion');
        candidateFields.add('city');
        candidateFields.add('latitudeLongitude');
      }

      const ops: QueuePatchOp[] = [];
      candidateFields.forEach(field => {
        if (!isSheetPatchField(field)) {
          return;
        }
        const patchField = field;
        const previousValue = previous[field];
        const nextValue = resolvedRow[field] ?? null;
        if (!isEqual(previousValue ?? null, nextValue)) {
          ops.push({
            rowId: row.rowId,
            field: patchField,
            value: nextValue,
            previousValue,
          });
        }
      });

      if (ops.length > 0) {
        options?.onOps?.(ops);
        enqueuePatchOps(ops);
      }
    },
    [enqueuePatchOps, multiPasteResolveMode, manualResolveMode, resolveSamplingPointRef, rowsById],
  );
  const [rowsToAddCount, setRowsToAddCount] = useState<number>(500);
  const [rangeSummary, setRangeSummary] = useState<RangeSelectionSummary | null>(null);
  const [selectedRowIds, setSelectedRowIds] = useState<ReadonlySet<string>>(() => new Set());
  const [activeFilterColIds, setActiveFilterColIds] = useState<string[]>([]);
  const [filteredRowCount, setFilteredRowCount] = useState<number | null>(null);
  const invalidOrganisationId = organisationIdParam != null && organisationId === undefined;
  const {
    data: samplingPointChoices,
    isLoading: isSamplingPointChoicesLoading,
    error: samplingPointChoicesError,
    refetch: refetchSamplingPointChoices,
  } = useOrgSamplingPointChoices(organisationId);
  const hasSamplingPointChoicesError = samplingPointChoicesError != null;
  const hasLoadedSamplingPointChoices = samplingPointChoices != null;
  const samplingPointChoicesErrorMessage =
    samplingPointChoicesError instanceof Error ? samplingPointChoicesError.message : null;
  const genePanels = useMemo(() => samplingPointChoices?.genePanels ?? [], [samplingPointChoices?.genePanels]);
  const linkedGenePanel = useMemo(
    () => (genePanelId != null ? genePanels.find(panel => panel.id === genePanelId) ?? null : null),
    [genePanelId, genePanels],
  );
  const shouldAsteriskFilteredVolume = useMemo(() => {
    const filledSampleTypes = rows
      .map(row => toSampleType(row.sampleType))
      .filter((sampleType): sampleType is SampleType => sampleType != null);
    if (filledSampleTypes.length === 0) {
      return false;
    }
    const hasWaterSample = filledSampleTypes.some(
      sampleType => sampleType !== SampleType.CONTROL && isWaterType(sampleType),
    );
    if (!hasWaterSample) {
      return false;
    }
    const hasNonWaterNonControlSample = filledSampleTypes.some(
      sampleType => sampleType !== SampleType.CONTROL && !isWaterType(sampleType),
    );
    return !hasNonWaterNonControlSample;
  }, [rows]);
  const dnaQualityHeaderFields = useMemo(() => {
    const hasValidNumber = (value: unknown): boolean => {
      const parsed = parseNumberCell(value);
      return typeof parsed === 'number' && Number.isFinite(parsed);
    };

    const elutedPair: ReadonlyArray<keyof SampleSpecRow> = ['primElutedDnaQuality280', 'primElutedDnaQuality230'];
    const dilutedPair: ReadonlyArray<keyof SampleSpecRow> = ['primDilutedDnaQuality280', 'primDilutedDnaQuality230'];
    const defaultPair = elutedPair;

    const resolvePairFromRow = (row: SampleSheetRowDraft) => {
      const eluted280 = hasValidNumber(row.primElutedDnaQuality280);
      const eluted230 = hasValidNumber(row.primElutedDnaQuality230);
      const diluted280 = hasValidNumber(row.primDilutedDnaQuality280);
      const diluted230 = hasValidNumber(row.primDilutedDnaQuality230);

      const hasElutedPair = eluted280 && eluted230;
      const hasDilutedPair = diluted280 && diluted230;
      if (!hasElutedPair && !hasDilutedPair) {
        return null;
      }
      if (hasElutedPair && hasDilutedPair) {
        return [...elutedPair, ...dilutedPair] satisfies Array<keyof SampleSpecRow>;
      }
      return hasElutedPair
        ? ([...elutedPair] satisfies Array<keyof SampleSpecRow>)
        : ([...dilutedPair] satisfies Array<keyof SampleSpecRow>);
    };

    const pickFrom = (candidates: readonly SampleSheetRowDraft[]) => {
      for (const row of candidates) {
        const resolved = resolvePairFromRow(row);
        if (resolved) {
          return resolved;
        }
      }
      return null;
    };

    const nonSubmitted = rows.filter(row => !row.isReadOnly);
    const submitted = rows.filter(row => row.isReadOnly);
    return pickFrom(nonSubmitted) ?? pickFrom(submitted) ?? [...defaultPair];
  }, [rows]);
  const dilutedVolumeMin = useMemo(() => {
    const workbookCfg =
      labWorkbookData?.smartChip?.configurationId ?? labWorkbookData?.primerPrep?.configurationId ?? null;
    return workbookCfg ? getMinimumDilutedDnaVolume(workbookCfg) : 100; // default when no template is selected
  }, [labWorkbookData?.primerPrep?.configurationId, labWorkbookData?.smartChip?.configurationId]);
  const dilutedVolumeAbsoluteMin = useMemo(() => {
    const workbookCfg =
      labWorkbookData?.smartChip?.configurationId ?? labWorkbookData?.primerPrep?.configurationId ?? null;
    return workbookCfg ? getAbsoluteMinimumDilutedDnaVolume(workbookCfg) : null;
  }, [labWorkbookData?.primerPrep?.configurationId, labWorkbookData?.smartChip?.configurationId]);
  const allowMissingPrimaryDnaDetails = labWorkbookServicesAllowMissingPrimaryDnaDetails(
    labWorkbookData?.workbook.services,
  );
  const requirePrimaryElutedDnaVolume = labWorkbookServicesRequirePrimaryElutedDnaVolume(
    labWorkbookData?.workbook.services,
  );
  const asteriskedHeaderFields = useMemo(() => {
    const fields = new Set<keyof SampleSpecRow>([
      'sampleDescription',
      'sampleType',
      'detailedSampleType',
      'country',
      'sampleCollectionDate',
    ] as const);

    if (!allowMissingPrimaryDnaDetails || requirePrimaryElutedDnaVolume) {
      fields.add('primElutedDnaVolume');
    }

    if (!allowMissingPrimaryDnaDetails) {
      fields.add('primElutedDnaConcentration');
      fields.add('primDilutedDnaConcentration');
      fields.add('primDilutedDnaVolume');
    }

    if (shouldAsteriskFilteredVolume) {
      fields.add('filteredVolume');
    }

    if (!allowMissingPrimaryDnaDetails) {
      dnaQualityHeaderFields.forEach(field => {
        fields.add(field);
      });
    }

    return fields;
  }, [
    allowMissingPrimaryDnaDetails,
    dnaQualityHeaderFields,
    requirePrimaryElutedDnaVolume,
    shouldAsteriskFilteredVolume,
  ]);
  const submissionValidationContext = useMemo(
    () => ({
      allowConfirmableMetadataLimitations: true,
      dilutedVolumeMin,
      dilutedVolumeAbsoluteMin,
      allowMissingElutedDnaBypass: Boolean(isResistomapAdmin),
      allowMissingFilteredVolumeBypass: true,
      allowMissingPrimaryDnaDetails,
      requirePrimaryElutedDnaVolume,
    }),
    [
      allowMissingPrimaryDnaDetails,
      dilutedVolumeMin,
      dilutedVolumeAbsoluteMin,
      isResistomapAdmin,
      requirePrimaryElutedDnaVolume,
    ],
  );
  const linkedGenePanelState = linkedGenePanel?.state ?? null;
  const linkedGenePanelLabel = useMemo(() => {
    if (!linkedGenePanel) {
      return 'Not linked';
    }
    return `${linkedGenePanel.name} · ${getGenePanelBadgeLabel(linkedGenePanel.state)}`;
  }, [linkedGenePanel]);
  const linkedGenePanelColorScheme = getGenePanelBadgeColor(linkedGenePanelState);
  const linkedGenePanelDisplayedLabel = linkedGenePanel ? linkedGenePanelLabel : 'Select gene panel';
  const missingGenePanel = linkedGenePanel == null;
  const isGenePanelStateAllowed =
    linkedGenePanelState === GenePanelState.VALID || linkedGenePanelState === GenePanelState.LOCKED;
  const invalidGenePanel = Boolean(linkedGenePanel && !isGenePanelStateAllowed);
  const submittedSampleRowCount = useMemo(() => rows.filter(isSubmittedSampleLikeRow).length, [rows]);
  const hasSubmittedEditsInProgress = useMemo(
    () => canEditSubmittedSamples && rows.some(row => isSubmittedSampleLikeRow(row) && !row.isReadOnly),
    [canEditSubmittedSamples, rows],
  );
  const hasSubmittedRowsEligibleForEditSession = useMemo(() => hasSubmittedRowsEligibleForEditing(rows), [rows]);
  const isInitialLoading =
    isSamplingPointChoicesLoading && !hasLoadedSamplingPointChoices && !hasSamplingPointChoicesError;
  const hasSubmittedSamples = submittedSampleRowCount > 0 || (labWorkbookData?.sampleProgress.submitted ?? 0) > 0;
  const canConfigureFirstSampleNumber =
    mode === 'samples' && canEditSubmittedSamples && resolvedLabWorkbookId != null && !hasSubmittedSamples;
  const sampleNumberingContinuationState = resolveSampleNumberingContinuationState({
    hasSubmittedSamples,
    canConfigureFirstSampleNumber,
  });
  const firstSampleNumberError = useMemo(() => {
    if (!canConfigureFirstSampleNumber) {
      return null;
    }
    const trimmed = firstSampleNumberInput.trim();
    if (trimmed.length === 0) {
      return 'Enter a positive integer.';
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed <= 0) {
      return 'Enter a positive integer.';
    }
    return null;
  }, [canConfigureFirstSampleNumber, firstSampleNumberInput]);
  const isFirstSampleNumberDirty =
    canConfigureFirstSampleNumber &&
    isFirstSampleNumberEdited({
      input: firstSampleNumberInput,
      configuredFirstSampleNumber,
      hasValidationError: firstSampleNumberError != null,
    });
  const handleSaveFirstSampleNumber = useCallback(async () => {
    if (!canConfigureFirstSampleNumber || !resolvedLabWorkbookId) {
      return;
    }
    if (firstSampleNumberError) {
      showToast({
        status: 'warning',
        title: 'Invalid first sample number',
        description: firstSampleNumberError,
      });
      return;
    }

    const nextFirstSampleNumber = Number(firstSampleNumberInput.trim());
    if (!isFirstSampleNumberDirty) {
      return;
    }

    try {
      const summary = await updateLabWorkbookMetadata.mutateAsync({
        workbookId: resolvedLabWorkbookId,
        body: { firstSampleNumber: nextFirstSampleNumber },
      });
      queryClient.setQueryData(['labWorkbook', resolvedLabWorkbookId], summary);
      await queryClient.invalidateQueries({ queryKey: ['labWorkbook', resolvedLabWorkbookId] });
      showToast({
        status: 'success',
        title: 'First sample number updated',
        description: `Draft numbering will start from ${nextFirstSampleNumber}.`,
      });
    } catch (error) {
      const description = error instanceof Error ? error.message : 'Unknown error';
      showToast({
        status: 'error',
        title: 'Failed to update first sample number',
        description,
      });
    }
  }, [
    canConfigureFirstSampleNumber,
    firstSampleNumberError,
    firstSampleNumberInput,
    isFirstSampleNumberDirty,
    queryClient,
    resolvedLabWorkbookId,
    showToast,
    updateLabWorkbookMetadata,
  ]);

  useEffect(() => {
    if (!canEditSubmittedSamples) {
      return;
    }
    if (!hasSubmittedEditsInProgress) {
      return;
    }
    setAdminRowViewMode(prev => (prev === 'submitted' ? prev : 'submitted'));
  }, [canEditSubmittedSamples, hasSubmittedEditsInProgress]);

  useEffect(() => {
    if (!canEditSubmittedSamples) {
      return;
    }
    if (!hasSubmittedEditsInProgress || adminRowViewMode !== 'submitted') {
      return;
    }

    const samplingPointIds = Array.from(
      new Set(
        rows
          .filter(row => isSubmittedSampleLikeRow(row) && !row.isReadOnly)
          .map(row => row.samplingPointId)
          .filter((id): id is number => typeof id === 'number' && Number.isFinite(id) && id > 0),
      ),
    );

    const missingIds = samplingPointIds.filter(
      id => !samplingPointUsageByIdRef.current.has(id) && !samplingPointUsageInFlightRef.current.has(id),
    );

    if (missingIds.length === 0) {
      return;
    }

    void fetchSamplingPointUsage(missingIds).catch(error => {
      const message = error instanceof Error ? error.message : 'Unknown error';
      showToast({
        status: 'error',
        title: 'Failed to load sampling point usage',
        description: message,
      });
    });
  }, [
    adminRowViewMode,
    canEditSubmittedSamples,
    fetchSamplingPointUsage,
    hasSubmittedEditsInProgress,
    rows,
    showToast,
  ]);

  const handleLinkGenePanel = useCallback(
    async (panelId: number | null) => {
      if (!sheetId) {
        showToast({
          status: 'error',
          title: 'Unable to link gene panel',
          description: 'Sample sheet is not ready yet.',
        });
        return;
      }
      setIsLinkingGenePanel(true);
      try {
        await resistomapApiClient.patch(
          `/admin/sample-sheets/${sheetId}/gene-panel`,
          {
            genePanelId: panelId,
          },
          {
            headers: getAuthHeaders('application/json'),
          },
        );
        void refetchSamplingPointChoices();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to link gene panel';
        showToast({ status: 'error', title: 'Failed to link gene panel', description: message });
      } finally {
        setIsLinkingGenePanel(false);
      }
    },
    [refetchSamplingPointChoices, sheetId, showToast],
  );

  useEffect(() => {
    if (!samplingPointChoicesErrorMessage) {
      return;
    }
    showToast({
      status: 'error',
      title: 'Failed to load sampling point choices',
      description: samplingPointChoicesErrorMessage,
    });
  }, [samplingPointChoicesErrorMessage, showToast]);

  const samplingPointOptions = useMemo<SamplingPointOptions>(() => {
    const emptyOptions: SamplingPointOptions = {
      labels: [],
      labelToSmplPntId: {},
      envById: {},
      envKeyById: {},
      envIdsByKey: {},
      labelBySmplPntId: {},
      envIdsByName: {},
      draftByKey: {},
      draftKeyByLabel: {},
    };

    const samplingPoints = samplingPointChoices?.samplingPoints ?? [];
    const envById: Record<number, SamplingPointSamplingPointData | undefined> = {};
    const envKeyById: Record<number, string | undefined> = {};
    const envIdsByKey: Record<string, number[] | undefined> = {};
    const envIdsByName: Record<string, number[] | undefined> = {};
    const labelToSmplPntId: Record<string, number | undefined> = {};
    const labelBySmplPntId: Record<number, string | undefined> = {};
    const draftByKey: Record<string, NormalizedSamplingPointFields | undefined> = {};
    const draftKeyByLabel: Record<string, string | undefined> = {};
    const nameCounts = new Map<string, number>();
    const nameTypeCounts = new Map<string, number>();

    samplingPoints.forEach(point => {
      const latitudeLongitude = point.latitudeLongitude ? { ...point.latitudeLongitude } : null;
      const samplingPoint: SamplingPointSamplingPointData = {
        samplingPointId: point.id,
        sampleDescription: point.name,
        sampleType: point.sampleType,
        detailedSampleType: point.detailedSampleType ?? null,
        country: point.country ?? null,
        stateOrRegion: point.stateOrRegion ?? null,
        city: point.city ?? null,
        latitudeLongitude,
      };
      envById[point.id] = samplingPoint;

      const rawBaseName = samplingPoint.sampleDescription;
      const baseName = rawBaseName == null ? '' : rawBaseName;
      nameCounts.set(baseName, (nameCounts.get(baseName) ?? 0) + 1);
      const typeKey = `${baseName}|||${samplingPoint.sampleType}`;
      nameTypeCounts.set(typeKey, (nameTypeCounts.get(typeKey) ?? 0) + 1);

      if (baseName) {
        const normalizedName = baseName.toLowerCase();
        const existing = envIdsByName[normalizedName];
        if (existing) {
          existing.push(point.id);
        } else {
          envIdsByName[normalizedName] = [point.id];
        }
      }

      const uniqueKey = buildSamplingPointUniqueKey(samplingPoint);
      if (uniqueKey) {
        const existing = envIdsByKey[uniqueKey];
        if (existing) {
          existing.push(point.id);
        } else {
          envIdsByKey[uniqueKey] = [point.id];
        }
        envKeyById[point.id] = uniqueKey;
      }
    });

    const seenDraftKeys = new Set<string>();
    rows.forEach(row => {
      if (row.samplingPointId != null) {
        return;
      }
      const uniqueKey = buildSamplingPointUniqueKey(row);
      if (!uniqueKey || seenDraftKeys.has(uniqueKey)) {
        return;
      }
      const normalized = normalizeSamplingPointFields(row);
      if (!normalized.sampleDescription || !normalized.sampleType) {
        return;
      }
      seenDraftKeys.add(uniqueKey);
      draftByKey[uniqueKey] = {
        ...normalized,
        latitudeLongitude: normalized.latitudeLongitude ? { ...normalized.latitudeLongitude } : null,
      };
      const baseName = normalized.sampleDescription;
      nameCounts.set(baseName, (nameCounts.get(baseName) ?? 0) + 1);
      const typeKey = `${baseName}|||${normalized.sampleType}`;
      nameTypeCounts.set(typeKey, (nameTypeCounts.get(typeKey) ?? 0) + 1);
    });

    type LabelEntry =
      | {
          kind: 'existing';
          baseName: string;
          sampleType: SampleSheetRowDraft['sampleType'];
          detailedSampleType: SampleSheetRowDraft['detailedSampleType'];
          samplingPointId: number;
        }
      | {
          kind: 'draft';
          baseName: string;
          sampleType: SampleSheetRowDraft['sampleType'];
          detailedSampleType: SampleSheetRowDraft['detailedSampleType'];
          draftKey: string;
        };

    const labelEntries: LabelEntry[] = [];
    Object.values(envById)
      .filter((samplingPoint): samplingPoint is SamplingPointSamplingPointData => samplingPoint != null)
      .forEach(samplingPoint => {
        const existingDescription = samplingPoint.sampleDescription;
        const safeDescription = existingDescription == null ? '' : existingDescription;
        labelEntries.push({
          kind: 'existing',
          baseName: safeDescription,
          sampleType: samplingPoint.sampleType,
          detailedSampleType: samplingPoint.detailedSampleType ?? null,
          samplingPointId: samplingPoint.samplingPointId,
        });
      });
    Object.entries(draftByKey).forEach(([draftKey, fields]) => {
      if (!fields || !fields.sampleDescription) {
        return;
      }
      const draftSampleType = fields.sampleType;
      if (!draftSampleType) {
        return;
      }
      labelEntries.push({
        kind: 'draft',
        baseName: fields.sampleDescription,
        sampleType: draftSampleType,
        detailedSampleType: fields.detailedSampleType,
        draftKey,
      });
    });

    if (labelEntries.length === 0) {
      return {
        ...emptyOptions,
        envById,
        envKeyById,
        envIdsByKey,
        envIdsByName,
        labelToSmplPntId,
        labelBySmplPntId,
        draftByKey,
        draftKeyByLabel,
      };
    }

    const labels: string[] = [];
    labelEntries
      .sort((left, right) => left.baseName.localeCompare(right.baseName, undefined, { sensitivity: 'base' }))
      .forEach(entry => {
        const baseName = entry.baseName;
        let label = baseName;
        if (label.length === 0) {
          label = entry.kind === 'draft' ? 'Draft sampling point' : `Sampling point #${entry.samplingPointId}`;
        }
        if (baseName) {
          const nameCount = nameCounts.get(baseName) ?? 0;
          if (nameCount > 1) {
            const typeLabel = formatSampleTypeLabel(entry.sampleType as SampleType | null);
            if (typeLabel) {
              label = `${label} – ${typeLabel}`;
            }
            const typeKey = `${baseName}|||${entry.sampleType}`;
            const nameTypeCount = nameTypeCounts.get(typeKey) ?? 0;
            if (nameTypeCount > 1) {
              const subtypeLabel = formatSamplingPointSubTypeLabel(
                entry.detailedSampleType,
                (entry.sampleType ?? null) as SampleType | null,
              );
              if (subtypeLabel) {
                label = `${label} – ${subtypeLabel}`;
              }
            }
          }
        }

        let uniqueLabel = label;
        let suffix = 2;
        while (
          Object.prototype.hasOwnProperty.call(labelToSmplPntId, uniqueLabel) ||
          Object.prototype.hasOwnProperty.call(draftKeyByLabel, uniqueLabel)
        ) {
          uniqueLabel = `${label} (${suffix})`;
          suffix += 1;
        }

        if (entry.kind === 'existing') {
          labelToSmplPntId[uniqueLabel] = entry.samplingPointId;
          labelBySmplPntId[entry.samplingPointId] = uniqueLabel;
        } else {
          draftKeyByLabel[uniqueLabel] = entry.draftKey;
        }
        labels.push(uniqueLabel);
      });

    return {
      labels,
      labelToSmplPntId,
      envById,
      envKeyById,
      envIdsByKey,
      labelBySmplPntId,
      envIdsByName,
      draftByKey,
      draftKeyByLabel,
    } satisfies SamplingPointOptions;
  }, [rows, samplingPointChoices]);

  const samplingPointFieldSet = useMemo(() => new Set<keyof SampleSheetRowDraft>(samplingPointFieldKeys), []);

  const beginSubmittedSamplingPointEdit = useCallback(
    async (args: {
      api: GridApi<SampleSheetRowDraft>;
      rowId: string;
      rowIndex: number | null | undefined;
      colId: string;
      field: keyof SampleSheetRowDraft;
      eventKey?: string;
    }) => {
      if (!canEditSubmittedSamples) {
        return;
      }
      if (blockedSamplingPointEdit) {
        return;
      }
      if (!samplingPointFieldSet.has(args.field)) {
        return;
      }

      const row = rowsByIdRef.current.get(args.rowId);
      if (!row || !isSubmittedSampleLikeRow(row) || row.isReadOnly) {
        return;
      }

      if (row.hasSpecificCoordinates) {
        showToast({
          status: 'warning',
          title: 'Cannot edit sampling point here',
          description: 'This sample uses specific coordinates. Edit the sampling point via the admin pages instead.',
        });
        return;
      }

      const rowIndex = args.rowIndex;
      if (rowIndex == null) {
        return;
      }

      const samplingPointId =
        typeof row.samplingPointId === 'number' && Number.isFinite(row.samplingPointId) ? row.samplingPointId : null;
      if (samplingPointId == null) {
        args.api.startEditingCell({ rowIndex, colKey: args.colId, eventKey: args.eventKey });
        return;
      }

      try {
        const usage = await ensureSamplingPointUsage(samplingPointId);
        if (!usage) {
          return;
        }
        if (usage.isExclusive) {
          args.api.startEditingCell({ rowIndex, colKey: args.colId, eventKey: args.eventKey });
          return;
        }

        pendingSamplingPointEditTargetRef.current = {
          rowId: args.rowId,
          colId: args.colId,
          eventKey: args.eventKey,
        };
        setBlockedSamplingPointEdit({ rowId: args.rowId, samplingPointId, field: args.field, usage });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        showToast({
          status: 'error',
          title: 'Failed to check sampling point usage',
          description: message,
        });
      }
    },
    [
      blockedSamplingPointEdit,
      canEditSubmittedSamples,
      ensureSamplingPointUsage,
      samplingPointFieldSet,
      showToast,
      pendingSamplingPointEditTargetRef,
    ],
  );

  const resolveSamplingPoint = useCallback(
    (row: SampleSheetRowDraft, resolveMode: ResolveMode): SampleSheetRowDraft => {
      const { envById, envKeyById, envIdsByKey } = samplingPointOptions;

      let workingRow = row;
      const baselineSnapshot: Partial<Record<keyof SampleSheetRowDraft, unknown>> = { ...row };
      const recordDiffIfNeeded = (updated: SampleSheetRowDraft): SampleSheetRowDraft => {
        const changes: DropdownRowMutation[] = [];
        samplingPointFieldKeys.forEach(field => {
          const previousValue = baselineSnapshot[field];
          const nextValue = updated[field];
          if (isEqual(previousValue ?? null, nextValue ?? null)) {
            return;
          }
          changes.push({ field, previousValue, newValue: nextValue });
        });
        if (!isEqual(baselineSnapshot.samplingPointId ?? null, updated.samplingPointId ?? null)) {
          changes.push({
            field: 'samplingPointId',
            previousValue: baselineSnapshot.samplingPointId,
            newValue: updated.samplingPointId,
          });
        }
        if (changes.length > 0) {
          recordExternalRowMutations(updated, changes);
        }
        return updated;
      };

      if (workingRow.samplingPointId != null) {
        const samplingPoint = envById[workingRow.samplingPointId];
        if (!samplingPoint) {
          workingRow = recordDiffIfNeeded({ ...workingRow, samplingPointId: null } satisfies SampleSheetRowDraft);
        } else {
          const rowKey = buildSamplingPointUniqueKey(workingRow);
          const expectedKey = envKeyById[workingRow.samplingPointId] ?? buildSamplingPointUniqueKey(samplingPoint);
          const matches =
            (expectedKey && rowKey === expectedKey) || samplingPointFieldsEqual(workingRow, samplingPoint);
          if (matches) {
            return recordDiffIfNeeded(applySamplingPointToRow(workingRow, samplingPoint));
          }
          workingRow = recordDiffIfNeeded({ ...workingRow, samplingPointId: null } satisfies SampleSheetRowDraft);
        }
      }

      if (resolveMode.allowByKey) {
        const candidateKey = buildSamplingPointUniqueKey(workingRow);
        if (candidateKey) {
          const matchedSmplPntIds = envIdsByKey[candidateKey];
          if (matchedSmplPntIds && matchedSmplPntIds.length === 1) {
            const samplingPoint = envById[matchedSmplPntIds[0]];
            if (samplingPoint) {
              return recordDiffIfNeeded(applySamplingPointToRow(workingRow, samplingPoint));
            }
          }
        }
      }

      return recordDiffIfNeeded(workingRow);
    },
    [recordExternalRowMutations, samplingPointOptions],
  );

  useEffect(() => {
    resolveSamplingPointRef.current = resolveSamplingPoint;
  }, [resolveSamplingPoint]);

  const validationIssuesByRow = useMemo(() => {
    const issuesByRow = new Map<string, RowValidationIssues>();
    rows.forEach(row => {
      if (isSubmittedSampleLikeRow(row)) {
        issuesByRow.set(row.rowId, row.validation);
        return;
      }

      const currentIssues = Object.fromEntries(
        Object.entries(row.validation).filter(
          ([field]) => !sampleMetadataNumberFieldKeys.includes(field as (typeof sampleMetadataNumberFieldKeys)[number]),
        ),
      ) as RowValidationIssues;
      const computedIssues = collectRowValidationIssues(row, submissionValidationContext);
      sampleMetadataNumberFieldKeys.forEach(field => {
        const fieldIssues = computedIssues[field];
        if (Array.isArray(fieldIssues) && fieldIssues.length > 0) {
          currentIssues[field] = fieldIssues;
        }
      });
      issuesByRow.set(row.rowId, currentIssues);
    });
    return issuesByRow;
  }, [rows, submissionValidationContext]);

  const warningsByRow = useMemo(() => {
    const warnings = new Map<string, RowValidationIssues>();
    rows.forEach(row => {
      const softWarnings = normalizeSoftWarnings(Reflect.get(row, 'softWarnings'));
      if (softWarnings) {
        warnings.set(row.rowId, softWarnings);
      }
    });
    return warnings;
  }, [rows]);

  const getCellIssues = useCallback(
    (rowId: string, field: keyof SampleSheetRowDraft) => {
      const rowIssues = validationIssuesByRow.get(rowId);
      if (!rowIssues) {
        return null;
      }
      const collected: string[] = [];
      displayFieldKeys(field).forEach(key => {
        const issues = rowIssues[key];
        if (Array.isArray(issues)) {
          collected.push(...issues);
        }
      });
      return collected.length > 0 ? collected : null;
    },
    [validationIssuesByRow],
  );

  const getCellWarnings = useCallback(
    (rowId: string, field: keyof SampleSheetRowDraft) => {
      const rowWarnings = warningsByRow.get(rowId);
      if (!rowWarnings) {
        return null;
      }
      const collected: string[] = [];
      displayFieldKeys(field).forEach(key => {
        const fieldWarnings = rowWarnings[key];
        if (Array.isArray(fieldWarnings)) {
          collected.push(...fieldWarnings);
        }
      });
      return collected.length > 0 ? collected : null;
    },
    [warningsByRow],
  );

  const isCellPending = useCallback(
    (rowId: string, field: keyof SampleSheetRowDraft) =>
      displayFieldKeys(field).some(key => pendingCells.has(createCellKey(rowId, String(key)))),
    [pendingCells],
  );

  const isCellCorrected = useCallback(
    (rowId: string, field: keyof SampleSheetRowDraft) =>
      displayFieldKeys(field).some(key => correctedCells.has(createCellKey(rowId, String(key)))),
    [correctedCells],
  );

  const isCellTouched = useCallback(
    (rowId: string, field: keyof SampleSheetRowDraft) => {
      const hasPendingOrCorrected = displayFieldKeys(field).some(key => {
        const compositeKey = createCellKey(rowId, String(key));
        return pendingCells.has(compositeKey) || correctedCells.has(compositeKey);
      });
      if (hasPendingOrCorrected) {
        return true;
      }
      const row = rowsById.get(rowId);
      if (!row) {
        return false;
      }
      return displayFieldKeys(field).some(key => {
        const issues = row.validation[key];
        return Array.isArray(issues) && issues.length > 0;
      });
    },
    [correctedCells, pendingCells, rowsById],
  );

  const getCellIssuesRef = useLatestRef(getCellIssues);
  const getCellWarningsRef = useLatestRef(getCellWarnings);
  const isCellPendingRef = useLatestRef(isCellPending);
  const isCellCorrectedRef = useLatestRef(isCellCorrected);
  const isCellTouchedRef = useLatestRef(isCellTouched);

  const gridContainerRef = useRef<HTMLDivElement>(null);
  const floatingHorizontalScrollRef = useRef<HTMLDivElement>(null);
  const [floatingHorizontalScrollState, setFloatingHorizontalScrollState] = useState(
    hiddenFloatingHorizontalScrollState,
  );
  const getHorizontalScrollViewport = useCallback(
    () => gridContainerRef.current?.querySelector<HTMLElement>('.ag-body-horizontal-scroll-viewport') ?? null,
    [],
  );
  const updateFloatingHorizontalScroll = useCallback(() => {
    const container = gridContainerRef.current;
    const horizontalViewport = getHorizontalScrollViewport();
    if (!container || !horizontalViewport) {
      setFloatingHorizontalScrollState(previousState =>
        previousState.visible ? hiddenFloatingHorizontalScrollState : previousState,
      );
      return;
    }

    const gridRect = container.getBoundingClientRect();
    const scrollRect = horizontalViewport.getBoundingClientRect();
    const gridIntersectsViewport = gridRect.top < window.innerHeight && gridRect.bottom > 0;
    const hasHorizontalOverflow = horizontalViewport.scrollWidth > horizontalViewport.clientWidth + 1;
    const nativeScrollbarVisible = scrollRect.top >= 0 && scrollRect.bottom <= window.innerHeight;
    if (!gridIntersectsViewport || !hasHorizontalOverflow || nativeScrollbarVisible) {
      setFloatingHorizontalScrollState(previousState =>
        previousState.visible ? hiddenFloatingHorizontalScrollState : previousState,
      );
      return;
    }

    const left = Math.max(0, Math.round(scrollRect.left));
    const width = Math.max(0, Math.min(Math.round(scrollRect.width), window.innerWidth - left));
    const nextState = {
      visible: width > 0,
      left,
      width,
      contentWidth: horizontalViewport.scrollWidth,
    };
    setFloatingHorizontalScrollState(previousState =>
      previousState.visible === nextState.visible &&
      previousState.left === nextState.left &&
      previousState.width === nextState.width &&
      previousState.contentWidth === nextState.contentWidth
        ? previousState
        : nextState,
    );

    const floatingScroll = floatingHorizontalScrollRef.current;
    if (floatingScroll && floatingScroll.scrollLeft !== horizontalViewport.scrollLeft) {
      floatingScroll.scrollLeft = horizontalViewport.scrollLeft;
    }
  }, [getHorizontalScrollViewport]);
  const handleFloatingHorizontalScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const horizontalViewport = getHorizontalScrollViewport();
      if (!horizontalViewport) {
        return;
      }
      const scrollLeft = event.currentTarget.scrollLeft;
      if (horizontalViewport.scrollLeft !== scrollLeft) {
        horizontalViewport.scrollLeft = scrollLeft;
        horizontalViewport.dispatchEvent(new Event('scroll'));
      }
    },
    [getHorizontalScrollViewport],
  );
  const isMacPlatform = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return false;
    }
    return /Mac|iPhone|iPad|iPod/.test(navigator.platform);
  }, []);

  useEffect(() => {
    const container = gridContainerRef.current;
    if (!container) {
      return;
    }

    const handleWheel = (event: WheelEvent) => {
      // Only handle vertical scrolling (deltaY)
      if (event.deltaY === 0) {
        return;
      }

      const scrollViewport =
        container.querySelector<HTMLElement>('.ag-body-vertical-scroll-viewport') ??
        container.querySelector<HTMLElement>('.ag-grid-viewport');
      if (!scrollViewport) {
        return;
      }

      // Check if the grid can scroll vertically
      const canScrollUp = scrollViewport.scrollTop > 0;
      const canScrollDown = scrollViewport.scrollTop < scrollViewport.scrollHeight - scrollViewport.clientHeight;

      // If the grid can't scroll in the direction of the wheel event, propagate to window
      const scrollingDown = event.deltaY > 0;
      const scrollingUp = event.deltaY < 0;

      if ((scrollingDown && !canScrollDown) || (scrollingUp && !canScrollUp) || (!canScrollUp && !canScrollDown)) {
        // Prevent the grid from handling this event
        event.stopPropagation();
        event.preventDefault();
        // Scroll the window instead
        window.scrollBy({
          top: event.deltaY,
          left: event.deltaX,
          behavior: 'auto',
        });
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, []);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }
    api.refreshCells({ force: true });
  }, [correctedCells, pendingCells, samplingPointUsageCacheVersion, validationIssuesByRow, warningsByRow]);

  useEffect(() => {
    if (activeFilterColIds.length === 0) {
      setFilteredRowCount(null);
      return;
    }
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }
    setFilteredRowCount(api.getDisplayedRowCount());
  }, [activeFilterColIds, adminRowViewMode, rows]);

  const rowIsReadOnly = useCallback(
    (row: SampleSheetRowDraft) => {
      if (row.isReadOnly) {
        return true;
      }
      if (isSubmittedSampleLikeRow(row) && !canEditSubmittedSamples) {
        return true;
      }
      return false;
    },
    [canEditSubmittedSamples],
  );

  const canEditSubmittedSamplingPoint = useCallback(
    (rowId: string, samplingPointId: number | null): boolean => {
      const row = rowsByIdRef.current.get(rowId);
      if (!row || !isSubmittedSampleLikeRow(row) || row.isReadOnly || !canEditSubmittedSamples) {
        return false;
      }
      if (row.hasSpecificCoordinates) {
        return false;
      }
      if (samplingPointId == null) {
        return true;
      }
      return samplingPointUsageByIdRef.current.get(samplingPointId)?.isExclusive === true;
    },
    [canEditSubmittedSamples],
  );

  const { columnDefs: builtColumnDefs, snapDropdownValue } = useMemo(() => {
    return buildSampleSheetColumnDefs({
      getCellIssues: (rowId, field) => getCellIssuesRef.current(rowId, field),
      getCellWarnings: (rowId, field) => getCellWarningsRef.current(rowId, field),
      isCellPending: (rowId, field) => isCellPendingRef.current(rowId, field),
      isCellCorrected: (rowId, field) => isCellCorrectedRef.current(rowId, field),
      isCellTouched: (rowId, field) => isCellTouchedRef.current(rowId, field),
      countriesByAlpha3,
      isRowReadOnly: rowIsReadOnly,
      canEditSubmittedSamplingPoint,
      activeFilterFields: new Set(activeFilterColIds),
      getSamplingPointDraftState: (rowId: string) => getSamplingPointDraftStateRef.current(rowId),
      getSamplingPointHighlightState: (rowId: string) => samplingPointHighlightByRowIdRef.current.get(rowId) ?? null,
      dilutedVolumeMin,
      asteriskedHeaderFields,
      samplingPointOptions: {
        sentinelLabel: newSamplingPointSentinelLabel,
        labels: samplingPointOptions.labels,
        labelToSmplPntId: samplingPointOptions.labelToSmplPntId,
        labelBySmplPntId: samplingPointOptions.labelBySmplPntId,
        envById: samplingPointOptions.envById,
        envIdsByName: samplingPointOptions.envIdsByName,
        draftByKey: samplingPointOptions.draftByKey,
        draftKeyByLabel: samplingPointOptions.draftKeyByLabel,
      },
      shouldSuppressDropdownCanonicalSelect: () => isPastingRef.current,
      onDropdownRowMutation: recordExternalRowMutations,
      primaryDnaDetailsOptional: allowMissingPrimaryDnaDetails,
    });
  }, [
    allowMissingPrimaryDnaDetails,
    activeFilterColIds,
    countriesByAlpha3,
    getCellIssuesRef,
    getCellWarningsRef,
    isCellPendingRef,
    isCellCorrectedRef,
    isCellTouchedRef,
    getSamplingPointDraftStateRef,
    samplingPointHighlightByRowIdRef,
    asteriskedHeaderFields,
    dilutedVolumeMin,
    rowIsReadOnly,
    canEditSubmittedSamplingPoint,
    samplingPointOptions,
    recordExternalRowMutations,
  ]);

  const clipboardEffects = useSmartchipClipboard({
    gridRef,
    samplingPointFieldSet,
    resolveSamplingPointRef,
    beginSubmittedSamplingPointEdit,
    showToast,
    countryNameByAlpha3,
    snapDropdownValue,
    rowsByIdRef,
    queuePatch: enqueuePatchOps,
  });

  const {
    processDataFromClipboard,
    processCellForClipboard,
    processCellFromClipboard,
    onPasteStart: onPasteStartInner,
    onPasteEnd: onPasteEndInner,
  } = clipboardEffects;

  const applyEditAction = useCallback((action: EditAction, direction: 'undo' | 'redo') => {
    const api = gridRef.current?.api ?? null;
    if (!api) {
      return false;
    }
    const cells = direction === 'undo' ? [...action.cells].reverse() : action.cells;
    const guardRef = direction === 'undo' ? isPerformingManualUndoRef : isPerformingManualRedoRef;
    guardRef.current = true;
    try {
      let appliedCount = 0;
      cells.forEach(({ rowId, field, previousValue, newValue }) => {
        const node = api.getRowNode(rowId);
        if (!node) {
          return;
        }
        const value = direction === 'undo' ? previousValue : newValue;
        const cellKey = createCellKey(rowId, String(field));
        ignoredHistoryCellsRef.current.add(cellKey);
        node.setDataValue(String(field), value ?? null);
        appliedCount += 1;
      });
      return appliedCount > 0;
    } finally {
      guardRef.current = false;
    }
  }, []);

  const buildPatchOpsFromAction = useCallback((action: EditAction, direction: 'undo' | 'redo'): QueuePatchOp[] => {
    const opMap = new Map<string, QueuePatchOp>();
    const getValue = (cell: CellHistoryChange) =>
      direction === 'undo' ? snapshotCellValue(cell.previousValue ?? null) : snapshotCellValue(cell.newValue ?? null);
    const getPrevious = (cell: CellHistoryChange) =>
      direction === 'undo' ? snapshotCellValue(cell.newValue ?? null) : snapshotCellValue(cell.previousValue ?? null);

    action.cells.forEach(cell => {
      if (!isSheetPatchField(cell.field)) {
        return;
      }
      const opKey = `${cell.rowId}::${String(cell.field)}`;
      opMap.set(opKey, {
        rowId: cell.rowId,
        field: cell.field,
        value: getValue(cell),
        previousValue: getPrevious(cell),
      });
    });
    return Array.from(opMap.values());
  }, []);

  const performUndo = useCallback(() => {
    const api = gridRef.current?.api ?? null;
    if (!api) {
      return;
    }
    if (editHistoryRef.current.length === 0) {
      return;
    }
    if (api.getEditingCells().length > 0) {
      api.stopEditing();
    }
    const action = editHistoryRef.current.pop();
    if (!action) {
      return;
    }
    startBatch('undo');
    try {
      const applied = applyEditAction(action, 'undo');
      if (applied) {
        const ops = buildPatchOpsFromAction(action, 'undo');
        if (ops.length > 0) {
          enqueuePatchOps(ops);
        }
        redoHistoryRef.current.push(action);
      } else {
        editHistoryRef.current.push(action);
      }
    } finally {
      endBatch();
    }
  }, [applyEditAction, buildPatchOpsFromAction, enqueuePatchOps, endBatch, startBatch]);

  const performRedo = useCallback(() => {
    const api = gridRef.current?.api ?? null;
    if (!api) {
      return;
    }
    if (redoHistoryRef.current.length === 0) {
      return;
    }
    if (api.getEditingCells().length > 0) {
      api.stopEditing();
    }
    const action = redoHistoryRef.current.pop();
    if (!action) {
      return;
    }
    startBatch('redo');
    try {
      const applied = applyEditAction(action, 'redo');
      if (applied) {
        const ops = buildPatchOpsFromAction(action, 'redo');
        if (ops.length > 0) {
          enqueuePatchOps(ops);
        }
        editHistoryRef.current.push(action);
      } else {
        redoHistoryRef.current.push(action);
      }
    } finally {
      endBatch();
    }
  }, [applyEditAction, buildPatchOpsFromAction, enqueuePatchOps, endBatch, startBatch]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handleGlobalUndoKey = (event: globalThis.KeyboardEvent) => {
      if (!gridContainerRef.current) {
        return;
      }
      const target = event.target as Node | null;
      const hasFocusedCell = Boolean(gridRef.current?.api.getFocusedCell());
      if (!hasFocusedCell && (!target || !gridContainerRef.current.contains(target))) {
        return;
      }
      const key = typeof event.key === 'string' ? event.key.toLowerCase() : '';
      const primaryModifier = isMacPlatform ? event.metaKey : event.ctrlKey;
      if (!primaryModifier) {
        return;
      }
      const wantsUndo = key === 'z' && !event.shiftKey;
      const wantsRedo = (key === 'z' && event.shiftKey) || key === 'y';
      if (!wantsUndo && !wantsRedo) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (wantsUndo) {
        performUndo();
      } else {
        performRedo();
      }
    };
    window.addEventListener('keydown', handleGlobalUndoKey, true);
    return () => {
      window.removeEventListener('keydown', handleGlobalUndoKey, true);
    };
  }, [isMacPlatform, performRedo, performUndo]);

  const onPasteStart = useCallback(
    (event: PasteStartEvent<SampleSheetRowDraft>) => {
      isPastingRef.current = true;
      startBatch('paste');
      onPasteStartInner(event);
    },
    [onPasteStartInner, startBatch],
  );

  const onPasteEnd = useCallback(
    (event: PasteEndEvent<SampleSheetRowDraft>) => {
      isPastingRef.current = false;
      try {
        onPasteEndInner(event);
      } finally {
        endBatch();
        maybeEnsureRowCapacity('paste');
      }
    },
    [endBatch, maybeEnsureRowCapacity, onPasteEndInner],
  );

  const handleFillStart = useCallback(
    (_event: FillStartEvent<SampleSheetRowDraft>) => {
      startBatch('fill');
    },
    [startBatch],
  );

  const handleFillEnd = useCallback(
    (_event: FillEndEvent<SampleSheetRowDraft>) => {
      endBatch();
    },
    [endBatch],
  );
  const defaultColDef = useMemo<ColDef<SampleSheetRowDraft>>(
    () => ({
      editable: true,
      resizable: true,
      minWidth: 140,
      sortable: false,
      filter: 'agMultiColumnFilter',
      enableRowGroup: true,
      enablePivot: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      suppressKeyboardEvent: params => {
        const rawKey = (params.event as { key?: unknown } | undefined)?.key;
        const key = typeof rawKey === 'string' ? rawKey : null;
        if (!key) {
          return false;
        }
        if (params.editing) {
          return false;
        }
        return key === 'Backspace' || key === 'Delete';
      },
    }),
    [],
  );
  const cellSelection = useMemo<CellSelectionOptions<SampleSheetRowDraft>>(
    () => ({
      mode: 'range',
      handle: {
        mode: 'fill',
      },
    }),
    [],
  );
  const rowSelection = useMemo<RowSelectionOptions<SampleSheetRowDraft>>(
    () => ({
      mode: 'singleRow',
      enableClickSelection: false,
      checkboxes: false,
    }),
    [],
  );
  const gridContext = useMemo<GridContextShape>(
    () => ({
      sampleSheet: {
        moveSelectionAfterEdit: requestMoveSelectionAfterEdit,
      },
    }),
    [requestMoveSelectionAfterEdit],
  );
  const getRowClass = useCallback(
    (rowParams: RowClassParams<SampleSheetRowDraft>) => {
      const row = rowParams.data;
      if (row && rowIsReadOnly(row)) {
        return 'sample-sheet-readonly-row';
      }
      return undefined;
    },
    [rowIsReadOnly],
  );

  // Anchor for Shift-click row range selection via the row index gutter
  const rowSelectionAnchorIndexRef = useRef<number | null>(null);
  const pendingSelectionRestoreRef = useRef<SelectionSnapshot | null>(null);

  const onCellClicked = useCallback((event: CellClickedEvent<SampleSheetRowDraft>) => {
    if (event.column.getColId() !== ROW_INDEX_COL_ID) {
      return;
    }
    const api = event.api;
    const rowIndex = event.node.rowIndex;
    if (typeof rowIndex !== 'number') {
      return;
    }
    type AnyCol = { getColId(): string; isVisible(): boolean };
    const apiWithCols = api as GridApi<SampleSheetRowDraft> & { getColumns?: () => AnyCol[] };
    const allCols: AnyCol[] = typeof apiWithCols.getColumns === 'function' ? apiWithCols.getColumns() ?? [] : [];
    const visibleColIds = allCols
      .filter((c: AnyCol) => c.isVisible() && c.getColId() !== ROW_INDEX_COL_ID)
      .map((c: AnyCol) => c.getColId());
    if (visibleColIds.length === 0) {
      return;
    }
    const shiftKey = event.event instanceof MouseEvent ? event.event.shiftKey : false;
    let startIndex = rowIndex;
    let endIndex = rowIndex;
    if (shiftKey && rowSelectionAnchorIndexRef.current != null) {
      startIndex = Math.min(rowSelectionAnchorIndexRef.current, rowIndex);
      endIndex = Math.max(rowSelectionAnchorIndexRef.current, rowIndex);
    } else {
      // Set anchor on plain click
      rowSelectionAnchorIndexRef.current = rowIndex;
    }
    api.clearCellSelection();
    api.addCellRange({ rowStartIndex: startIndex, rowEndIndex: endIndex, columns: visibleColIds });
  }, []);

  const onCellDoubleClicked = useCallback(
    (event: CellDoubleClickedEvent<SampleSheetRowDraft>) => {
      const row = event.data;
      if (!row || !isSubmittedSampleLikeRow(row) || row.isReadOnly) {
        return;
      }
      const columnFieldFromDef = event.colDef.field;
      const field =
        typeof columnFieldFromDef === 'string' && columnFieldFromDef.length > 0
          ? columnFieldFromDef
          : event.column.getColId();
      const typedField = field as keyof SampleSheetRowDraft;
      if (!samplingPointFieldSet.has(typedField)) {
        return;
      }
      if (event.column.isCellEditable(event.node)) {
        return;
      }
      void beginSubmittedSamplingPointEdit({
        api: event.api,
        rowId: row.rowId,
        rowIndex: event.node.rowIndex,
        colId: event.column.getColId(),
        field: typedField,
      });
    },
    [beginSubmittedSamplingPointEdit, samplingPointFieldSet],
  );
  const visibleRows = useMemo(() => {
    return adminRowViewMode === 'submitted'
      ? rows.filter(isSubmittedSampleLikeRow)
      : rows.filter(row => !isSubmittedSampleLikeRow(row));
  }, [adminRowViewMode, rows]);
  const isLegacyBioReplicaMode = useMemo(() => {
    if (labWorkbookStatus === LabWorkbookStatus.LEGACY) {
      return true;
    }
    if (typeof labWorkbookLabSheetUrl === 'string' && labWorkbookLabSheetUrl.trim() !== '') {
      return true;
    }
    return rows.some(row => typeof row.bioReplica === 'string' && row.bioReplica.trim() !== '');
  }, [labWorkbookLabSheetUrl, labWorkbookStatus, rows]);

  const maxGridHeight = useBreakpointValue({ base: 360, sm: 420, md: 520, lg: 640, xl: 760 });
  const gridDomLayout = visibleRows.length <= PAGE_SCROLL_ROW_LIMIT ? 'autoHeight' : undefined;
  const gridHeight = useMemo(() => {
    if (gridDomLayout === 'autoHeight') {
      return undefined;
    }
    const headerHeight = 64;
    const rowHeight = 25;
    const minHeight = GRID_DEFAULT_MIN_HEIGHT; // Minimum height for empty table
    const desiredMaxHeight = maxGridHeight ?? 640;
    const maxRows = Math.max(1, Math.floor((desiredMaxHeight - headerHeight) / rowHeight));
    const snappedMaxHeight = headerHeight + maxRows * rowHeight;
    return Math.max(snappedMaxHeight, minHeight);
  }, [gridDomLayout, maxGridHeight]);

  useEffect(() => {
    if (mode !== 'samples' || isInitialLoading) {
      setFloatingHorizontalScrollState(previousState =>
        previousState.visible ? hiddenFloatingHorizontalScrollState : previousState,
      );
      return;
    }

    const update = () => {
      updateFloatingHorizontalScroll();
    };
    update();

    const container = gridContainerRef.current;
    const horizontalViewport = getHorizontalScrollViewport();
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            update();
          });
    if (container) {
      resizeObserver?.observe(container);
    }
    if (horizontalViewport) {
      resizeObserver?.observe(horizontalViewport);
      horizontalViewport.addEventListener('scroll', update);
    }
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    const frameId = window.requestAnimationFrame(update);
    const timeoutId = window.setTimeout(update, 250);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      horizontalViewport?.removeEventListener('scroll', update);
      resizeObserver?.disconnect();
    };
  }, [
    getHorizontalScrollViewport,
    gridDomLayout,
    isInitialLoading,
    mode,
    updateFloatingHorizontalScroll,
    visibleRows.length,
  ]);

  useEffect(() => {
    if (!floatingHorizontalScrollState.visible) {
      return;
    }
    const floatingScroll = floatingHorizontalScrollRef.current;
    const horizontalViewport = getHorizontalScrollViewport();
    if (floatingScroll && horizontalViewport && floatingScroll.scrollLeft !== horizontalViewport.scrollLeft) {
      floatingScroll.scrollLeft = horizontalViewport.scrollLeft;
    }
  }, [
    floatingHorizontalScrollState.contentWidth,
    floatingHorizontalScrollState.left,
    floatingHorizontalScrollState.visible,
    floatingHorizontalScrollState.width,
    getHorizontalScrollViewport,
  ]);

  const previewSampleLabelByRowId = useMemo(() => {
    const map = new Map<string, string>();
    const draftStartNumber = configuredFirstSampleNumber;

    if (!isLegacyBioReplicaMode) {
      const maxSubmittedNumber = rows.reduce((max, row) => {
        if (!hasServerLockedNumbering(row)) {
          return max;
        }
        const parsed = parseNumberCell(row.sampleNumber);
        if (typeof parsed !== 'number' || !Number.isFinite(parsed)) {
          return max;
        }
        return Math.max(max, parsed);
      }, 0);

      const startNumber = maxSubmittedNumber > 0 ? maxSubmittedNumber + 1 : draftStartNumber;

      visibleRows.forEach(row => {
        if (hasServerLockedNumbering(row)) {
          const n = parseNumberCell(row.sampleNumber);
          const bio = typeof row.bioReplica === 'string' ? row.bioReplica.trim().toUpperCase() : null;
          if (n != null || (bio && bio !== '')) {
            const label = n != null ? (bio ? `${n} ${bio}` : String(n)) : bio ?? '';
            map.set(row.rowId, label);
          } else {
            map.set(row.rowId, '');
          }
          return;
        }
      });

      const editableRows = visibleRows.filter(row => !hasServerLockedNumbering(row) && !rowIsReadOnly(row));
      editableRows.forEach((row, index) => {
        map.set(row.rowId, String(startNumber + index));
      });

      return map;
    }

    const usedNumbers = new Set<number>();
    rows.forEach(r => {
      const n = parseNumberCell(r.sampleNumber);
      if (n != null) {
        usedNumbers.add(n);
      }
    });
    let next = draftStartNumber;
    const advance = () => {
      while (usedNumbers.has(next)) next += 1;
    };
    advance();

    visibleRows.forEach(r => {
      const n = parseNumberCell(r.sampleNumber);
      const bio = typeof r.bioReplica === 'string' ? r.bioReplica.trim().toUpperCase() : null;
      if (n != null || (bio && bio !== '')) {
        const label = n != null ? (bio ? `${n} ${bio}` : String(n)) : bio ?? '';
        map.set(r.rowId, label);
        if (n != null && !usedNumbers.has(n)) {
          usedNumbers.add(n);
          advance();
        }
      } else {
        const label = `${next} A`;
        map.set(r.rowId, label);
        usedNumbers.add(next);
        next += 1;
        advance();
      }
    });
    return map;
  }, [configuredFirstSampleNumber, isLegacyBioReplicaMode, rowIsReadOnly, rows, visibleRows]);
  const previewSampleLabelByRowIdRef = useLatestRef(previewSampleLabelByRowId);
  const previewSampleNumberByRowId = useMemo(() => {
    const map = new Map<string, number | null>();
    previewSampleLabelByRowId.forEach((label, rowId) => {
      map.set(rowId, parseSampleNumberFromLabel(label));
    });
    return map;
  }, [previewSampleLabelByRowId]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }
    api.refreshCells({
      columns: [ROW_INDEX_COL_ID],
      force: true,
      suppressFlash: true,
    });
  }, [previewSampleLabelByRowId]);

  const visibleRowCountExcludingTrailingEmpty = useMemo(() => {
    let index = visibleRows.length - 1;
    while (index >= 0 && isRowEmpty(visibleRows[index])) {
      index -= 1;
    }
    return Math.max(index + 1, 0);
  }, [visibleRows]);
  const displayedRowSummary = useMemo(() => {
    if (filteredRowCount != null) {
      return `${filteredRowCount} filtered row${filteredRowCount === 1 ? '' : 's'} of ${visibleRowCountExcludingTrailingEmpty} displayed`;
    }
    // When not filtered, just show "X rows" (segments will add context like "total" or parenthetical info)
    return `${visibleRowCountExcludingTrailingEmpty} row${visibleRowCountExcludingTrailingEmpty === 1 ? '' : 's'}`;
  }, [filteredRowCount, visibleRowCountExcludingTrailingEmpty]);

  const onCellEditingStarted = useCallback((event: CellEditingStartedEvent<SampleSheetRowDraft>) => {
    const row = event.data;
    if (!row) {
      return;
    }
    activeEditingRowIdRef.current = row.rowId;
    linkedHistoryChangesRef.current = [];
    preEditRowsRef.current.set(row.rowId, cloneRow(row));
  }, []);

  const onCellEditingStopped = useCallback((event: CellEditingStoppedEvent<SampleSheetRowDraft>) => {
    const row = event.data;
    if (!row) {
      return;
    }
    preEditRowsRef.current.delete(row.rowId);
    activeEditingRowIdRef.current = null;
  }, []);

  const handleAddRows = useCallback(() => {
    const count = Number.isFinite(rowsToAddCount) && rowsToAddCount > 0 ? Math.floor(rowsToAddCount) : 1;
    void createRows(count).catch(error => {
      const message = error instanceof Error ? error.message : 'Failed to add rows';
      showToast({ status: 'error', title: 'Failed to add rows', description: message });
    });
  }, [createRows, rowsToAddCount, showToast]);

  const rowsReadyForSubmit = useMemo(
    () =>
      rows.filter(
        row =>
          !isSubmittedSampleLikeRow(row) && !row.isReadOnly && isRowReadyForSubmit(row, submissionValidationContext),
      ),
    [rows, submissionValidationContext],
  );
  const rowReadyForSubmitById = useMemo(() => new Set(rowsReadyForSubmit.map(row => row.rowId)), [rowsReadyForSubmit]);
  const orderedEditableRows = useMemo(() => {
    const editable = rows.filter(row => !isSubmittedSampleLikeRow(row) && !row.isReadOnly);
    return editable.slice().sort((left, right) => {
      const leftIndex = typeof left.rowIndex === 'number' ? left.rowIndex : Number.MAX_SAFE_INTEGER;
      const rightIndex = typeof right.rowIndex === 'number' ? right.rowIndex : Number.MAX_SAFE_INTEGER;
      if (leftIndex !== rightIndex) {
        return leftIndex - rightIndex;
      }
      return left.rowId.localeCompare(right.rowId);
    });
  }, [rows]);
  const hasMixedPrimaryDnaDetails = useMemo(
    () => hasMixedPrimaryDnaDetailCompletion(rows, submissionValidationContext),
    [rows, submissionValidationContext],
  );
  const submitSelection = useMemo(() => {
    const resolveRowSampleNumber = (row: SampleSheetRowDraft): number | null => {
      const preview = previewSampleNumberByRowId.get(row.rowId);
      if (preview != null) {
        return preview;
      }
      const parsed = parseNumberCell(row.sampleNumber);
      if (typeof parsed === 'number' && Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
      return null;
    };

    const orderedEditableMeta = orderedEditableRows.map((row, index) => ({
      rowId: row.rowId,
      index,
      sampleNumber: resolveRowSampleNumber(row),
    }));

    const selectedEditable = orderedEditableMeta.filter(meta => selectedRowIds.has(meta.rowId));
    if (selectedEditable.length === 0) {
      return {
        isContiguousPrefix: false,
        startsFromFirst: false,
        isContinuous: false,
        rowIdsToSubmit: [] as string[],
        hasIncompleteRows: false,
        firstUnsubmittedSampleNumber: null as number | null,
        lastSelectedSampleNumber: null as number | null,
        missingSampleNumbers: [] as number[],
      } as const;
    }

    const minUnsubmittedSampleNumber = orderedEditableMeta.reduce<number | null>((min, meta) => {
      const n = meta.sampleNumber;
      if (typeof n !== 'number' || !Number.isFinite(n)) {
        return min;
      }
      if (min == null) {
        return n;
      }
      return Math.min(min, n);
    }, null);

    const startRowId =
      minUnsubmittedSampleNumber != null
        ? orderedEditableMeta.find(meta => meta.sampleNumber === minUnsubmittedSampleNumber)?.rowId ?? null
        : null;

    const startsFromFirst = startRowId != null && selectedRowIds.has(startRowId);

    const selectedSampleNumbers = selectedEditable
      .map(meta => meta.sampleNumber)
      .filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
    const hasUnnumberedSelectedRow = selectedSampleNumbers.length !== selectedEditable.length;

    const maxSelectedSampleNumber =
      selectedSampleNumbers.length > 0 ? selectedSampleNumbers.reduce((max, n) => Math.max(max, n), 0) : null;

    const missingSampleNumbers: number[] = [];
    if (
      minUnsubmittedSampleNumber != null &&
      maxSelectedSampleNumber != null &&
      !hasUnnumberedSelectedRow &&
      Number.isFinite(minUnsubmittedSampleNumber) &&
      Number.isFinite(maxSelectedSampleNumber) &&
      maxSelectedSampleNumber >= minUnsubmittedSampleNumber
    ) {
      const selectedNumberSet = new Set(selectedSampleNumbers);
      for (let n = minUnsubmittedSampleNumber; n <= maxSelectedSampleNumber; n += 1) {
        if (!selectedNumberSet.has(n)) {
          missingSampleNumbers.push(n);
        }
      }
    }

    const expectedRowIdsToSubmit =
      startsFromFirst &&
      !hasUnnumberedSelectedRow &&
      minUnsubmittedSampleNumber != null &&
      maxSelectedSampleNumber != null
        ? orderedEditableMeta
            .filter(meta => {
              const n = meta.sampleNumber;
              return (
                typeof n === 'number' &&
                Number.isFinite(n) &&
                n >= minUnsubmittedSampleNumber &&
                n <= maxSelectedSampleNumber
              );
            })
            .map(meta => meta.rowId)
        : [];

    const selectionMatchesExpected =
      expectedRowIdsToSubmit.length > 0 &&
      expectedRowIdsToSubmit.length === selectedEditable.length &&
      expectedRowIdsToSubmit.every((rowId, index) => selectedEditable[index]?.rowId === rowId);

    const isContinuous = selectionMatchesExpected && missingSampleNumbers.length === 0;
    const isContiguousPrefix = startsFromFirst && isContinuous;
    const rowIdsToSubmit = isContiguousPrefix ? expectedRowIdsToSubmit : [];
    const hasIncompleteRows = rowIdsToSubmit.some(rowId => !rowReadyForSubmitById.has(rowId));

    return {
      isContiguousPrefix,
      startsFromFirst,
      isContinuous,
      rowIdsToSubmit,
      hasIncompleteRows,
      firstUnsubmittedSampleNumber: minUnsubmittedSampleNumber,
      lastSelectedSampleNumber: maxSelectedSampleNumber,
      missingSampleNumbers,
    } as const;
  }, [orderedEditableRows, previewSampleNumberByRowId, rowReadyForSubmitById, selectedRowIds]);
  const selectedRowsReadyForSubmit = useMemo(
    () => rowsReadyForSubmit.filter(row => selectedRowIds.has(row.rowId)),
    [rowsReadyForSubmit, selectedRowIds],
  );
  const incompleteRowCount = useMemo(
    () =>
      rows.reduce((count, row) => {
        if (isSubmittedSampleLikeRow(row)) {
          return count;
        }
        if (row.isReadOnly || isRowEmpty(row)) {
          return count;
        }
        if (isRowReadyForSubmit(row, submissionValidationContext)) {
          return count;
        }
        return count + 1;
      }, 0),
    [rows, submissionValidationContext],
  );
  const readyToSubmitCount = rowsReadyForSubmit.length;
  const selectedReadyToSubmitCount = selectedRowsReadyForSubmit.length;
  const disableEditing = hasSamplingPointChoicesError;
  const canSubmit =
    submitSelection.rowIdsToSubmit.length > 0 &&
    !submitSelection.hasIncompleteRows &&
    submitSelection.isContiguousPrefix &&
    !isFirstSampleNumberDirty &&
    !hasMixedPrimaryDnaDetails &&
    !missingGenePanel &&
    isGenePanelStateAllowed;
  const isEditingSubmittedSamples =
    canEditSubmittedSamples && adminRowViewMode === 'submitted' && hasSubmittedEditsInProgress;
  const submitRowsStable = useCallback(
    async (
      rowIds: string[],
      confirmations: SampleSheetSubmissionConfirmationOptions = {},
    ): Promise<SampleSheetSubmissionAttemptOutcome> => {
      const result = await submit(rowIds, confirmations);
      if (result.status === 'requires_metadata_limitations_confirmation') {
        if (confirmations.metadataLimitationsConfirmationToken === result.confirmationToken) {
          throw new Error('Metadata limitations confirmation failed to proceed. Please retry.');
        }
        setMetadataLimitationsAcknowledged(false);
        setPendingPartialVolumeConfirmation(null);
        setPendingMetadataLimitationsConfirmation({
          source: 'submit',
          rowIds,
          payload: result,
          confirmations,
        });
        return 'metadata_limitations_confirmation';
      }
      if (result.status === 'requires_partial_volume_confirmation') {
        if (confirmations.confirmProceedWithPartialVolumeNormalisation) {
          throw new Error('Partial volume confirmation failed to proceed. Please retry.');
        }
        setMetadataLimitationsAcknowledged(false);
        setPendingMetadataLimitationsConfirmation(null);
        setPendingPartialVolumeConfirmation({
          source: 'submit',
          rowIds,
          payload: result,
          confirmations,
        });
        return 'partial_volume_confirmation';
      }
      setPendingMetadataLimitationsConfirmation(null);
      setPendingPartialVolumeConfirmation(null);
      setPendingSubmitConfirmation(null);
      window.location.reload();
      return 'submitted';
    },
    [submit],
  );
  const handleSubmit = useCallback(async () => {
    if (missingGenePanel) {
      showToast({
        status: 'warning',
        title: 'Select genes before submitting',
        description: GENE_SELECTION_REQUIRED_MESSAGE,
      });
      return;
    }
    if (invalidGenePanel) {
      showToast({
        status: 'warning',
        title: 'Gene panel not ready',
        description: 'Verify the linked gene panel is valid before submitting.',
      });
      return;
    }
    if (isFirstSampleNumberDirty) {
      showToast({
        status: 'warning',
        title: 'Save the first sample number',
        description: 'Save or discard the edited first sample number before submitting.',
      });
      return;
    }
    if (selectedReadyToSubmitCount === 0) {
      const hasRangeWarningsInSelection =
        selectedRowIds.size > 0 &&
        Array.from(selectedRowIds).some(rowId => {
          const w = warningsByRow.get(rowId);
          return w && Object.values(w).some(arr => Array.isArray(arr) && arr.length > 0);
        });
      if (hasRangeWarningsInSelection) {
        showToast({
          status: 'warning',
          title: 'Rows have out-of-range values',
          description: 'Fix highlighted values to match accepted ranges before submitting.',
        });
      } else {
        showToast({
          status: 'info',
          title: 'Select rows to submit',
          description: 'Highlight at least one complete row before submitting.',
        });
      }
      return;
    }
    if (!submitSelection.startsFromFirst) {
      const firstNumber = submitSelection.firstUnsubmittedSampleNumber;
      showToast({
        status: 'warning',
        title: 'Submit from the first unsubmitted row',
        description:
          typeof firstNumber === 'number'
            ? `To keep sample numbering stable, start your selection from sample number ${firstNumber}.`
            : 'To keep sample numbering stable, start your selection from the first unsubmitted row.',
      });
      return;
    }
    if (!submitSelection.isContinuous) {
      const missing = submitSelection.missingSampleNumbers;
      const missingText =
        missing.length > 0
          ? ` Missing sample number${missing.length === 1 ? '' : 's'}: ${missing.slice(0, 5).join(', ')}${missing.length > 5 ? '…' : ''}.`
          : '';
      showToast({
        status: 'warning',
        title: 'Select a continuous block of rows',
        description: `To keep sample numbering stable, selection cannot have gaps between rows.${missingText}`,
      });
      return;
    }
    if (submitSelection.hasIncompleteRows) {
      showToast({
        status: 'warning',
        title: 'Complete earlier rows before submitting',
        description: 'To submit later rows, all selected earlier rows must be complete.',
      });
      return;
    }
    if (organisationId === undefined) {
      showToast({
        status: 'error',
        title: 'Missing organisation id',
        description: 'Refresh the page and try again.',
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const rowIds = submitSelection.rowIdsToSubmit;
      await submitRowsStable(rowIds, {
        expectedSampleNumbering:
          pendingSubmitConfirmation?.kind === 'numbering' ? pendingSubmitConfirmation : undefined,
      });
    } catch (error) {
      const description = error instanceof Error ? error.message : 'Unknown error';
      showToast({ status: 'error', title: 'Failed to submit sample sheet', description });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    invalidGenePanel,
    isFirstSampleNumberDirty,
    missingGenePanel,
    organisationId,
    selectedReadyToSubmitCount,
    selectedRowIds,
    pendingSubmitConfirmation,
    submitSelection,
    submitRowsStable,
    warningsByRow,
    showToast,
  ]);

  const handleSubmitClick = useCallback(() => {
    if (!canSubmit) {
      void handleSubmit();
      return;
    }
    if (!confirmsSampleNumbering) {
      setPendingSubmitConfirmation({ kind: 'assurance', count: selectedReadyToSubmitCount });
      return;
    }
    const first = submitSelection.firstUnsubmittedSampleNumber;
    const last = submitSelection.lastSelectedSampleNumber;
    if (first == null || last == null) {
      showToast({
        status: 'warning',
        title: 'Sample numbering is not ready',
        description: 'Refresh the page and try again.',
      });
      return;
    }
    setPendingSubmitConfirmation({ kind: 'numbering', first, last, count: selectedReadyToSubmitCount });
  }, [
    canSubmit,
    confirmsSampleNumbering,
    handleSubmit,
    selectedReadyToSubmitCount,
    showToast,
    submitSelection.firstUnsubmittedSampleNumber,
    submitSelection.lastSelectedSampleNumber,
  ]);

  const pendingEditCount = pendingCells.size;
  const hasDirtySubmittedEdits = useMemo(
    () =>
      canEditSubmittedSamples &&
      rows.some(row => isSubmittedSampleLikeRow(row) && !row.isReadOnly && row.submittedEditsDirty === true),
    [canEditSubmittedSamples, rows],
  );
  const hasBlockingSubmittedEdits = hasDirtySubmittedEdits || (isEditingSubmittedSamples && pendingEditCount > 0);
  const allEditableSubmittedRowIds = useMemo(() => {
    if (!isEditingSubmittedSamples) {
      return [] as string[];
    }
    return rows.filter(row => isSubmittedSampleLikeRow(row) && !row.isReadOnly).map(row => row.rowId);
  }, [isEditingSubmittedSamples, rows]);
  const allEditableSubmittedCount = allEditableSubmittedRowIds.length;
  const canResubmitSampleEdits =
    allEditableSubmittedCount > 0 && pendingEditCount === 0 && !missingGenePanel && isGenePanelStateAllowed;
  const syncIndicatorText = useMemo(() => {
    const details: string[] = [];
    if (pendingEditCount > 0) {
      details.push(`${pendingEditCount} pending ${pendingEditCount === 1 ? 'edit' : 'edits'}`);
    }
    const lagMs = connection.lagMs;
    if (isResistomapAdmin && lagMs != null) {
      details.push(`${lagMs} ms lag`);
    }
    if (details.length === 0) {
      return `Sync: ${connection.state}`;
    }
    return `Sync: ${connection.state} (${details.join(', ')})`;
  }, [connection.lagMs, connection.state, isResistomapAdmin, pendingEditCount]);

  const syncIndicatorColor = useMemo(
    () => (connection.state === 'connected' ? 'gray.500' : 'orange.500'),
    [connection.state],
  );
  const submitButtonTooltip = useMemo(() => {
    if (canSubmit) {
      return null;
    }
    if (missingGenePanel) {
      return GENE_SELECTION_REQUIRED_MESSAGE;
    }
    if (invalidGenePanel) {
      return 'Verify the linked gene panel is valid before submitting.';
    }
    if (disableEditing) {
      return 'Resolve sample sheet errors before submitting.';
    }
    if (hasMixedPrimaryDnaDetails) {
      return primaryDnaDetailWorkbookMixedMessage;
    }
    if (isFirstSampleNumberDirty) {
      return 'Save or discard the edited first sample number before submitting.';
    }
    if (selectedReadyToSubmitCount === 0) {
      return 'Click or drag the gray sample number column to select a continuous block of complete rows from the top.';
    }
    if (!submitSelection.startsFromFirst) {
      const firstNumber = submitSelection.firstUnsubmittedSampleNumber;
      return typeof firstNumber === 'number'
        ? `Start your selection from sample number ${firstNumber}.`
        : 'Start your selection from the first unsubmitted row.';
    }
    if (!submitSelection.isContinuous) {
      const missing = submitSelection.missingSampleNumbers;
      if (missing.length > 0) {
        const missingText = missing.slice(0, 3).join(', ');
        const suffix = missing.length > 3 ? '…' : '';
        return `Select a continuous block of rows (missing sample ${missing.length === 1 ? 'number' : 'numbers'}: ${missingText}${suffix}).`;
      }
      return 'Select a continuous block of rows (no gaps between rows).';
    }
    if (submitSelection.hasIncompleteRows) {
      return 'All selected rows must be complete to submit.';
    }
    return 'Unable to submit with the current selection.';
  }, [
    canSubmit,
    disableEditing,
    hasMixedPrimaryDnaDetails,
    invalidGenePanel,
    isFirstSampleNumberDirty,
    missingGenePanel,
    selectedReadyToSubmitCount,
    submitSelection.firstUnsubmittedSampleNumber,
    submitSelection.hasIncompleteRows,
    submitSelection.isContinuous,
    submitSelection.missingSampleNumbers,
    submitSelection.startsFromFirst,
  ]);

  const resubmitButtonTooltip = useMemo(() => {
    if (canResubmitSampleEdits) {
      return null;
    }
    if (missingGenePanel) {
      return GENE_SELECTION_REQUIRED_MESSAGE;
    }
    if (invalidGenePanel) {
      return 'Verify the linked gene panel is valid before submitting edits.';
    }
    if (disableEditing) {
      return 'Resolve sample sheet errors before submitting edits.';
    }
    if (pendingEditCount > 0) {
      return 'Wait for pending edits to sync before submitting edits.';
    }
    if (allEditableSubmittedCount === 0) {
      return 'No editable submitted rows available.';
    }
    return 'Unable to submit edits.';
  }, [
    allEditableSubmittedCount,
    canResubmitSampleEdits,
    disableEditing,
    invalidGenePanel,
    missingGenePanel,
    pendingEditCount,
  ]);

  const handleCancelSubmittedEdits = useCallback(() => {
    if (!canEditSubmittedSamples) {
      return;
    }
    if (pendingEditCount > 0) {
      showToast({
        status: 'warning',
        title: 'Wait for sync',
        description: 'Wait for pending edits to sync before cancelling submitted edits.',
      });
      return;
    }

    const api = gridRef.current?.api ?? null;
    pendingSelectionRestoreRef.current = null;
    api?.clearCellSelection();
    rowSelectionAnchorIndexRef.current = null;
    setRangeSummary(null);
    setSelectedRowIds(prev => (prev.size === 0 ? prev : new Set()));

    setIsCancellingSubmittedEdits(true);
    void cancelSampleEdits()
      .then(() => {
        showToast({
          status: 'success',
          title: 'Submitted edits cancelled',
          description: 'Unsubmitted changes were discarded.',
        });
      })
      .catch(error => {
        const description = error instanceof Error ? error.message : 'Unknown error';
        showToast({ status: 'error', title: 'Failed to cancel submitted edits', description });
      })
      .finally(() => {
        setIsCancellingSubmittedEdits(false);
      });
  }, [canEditSubmittedSamples, cancelSampleEdits, pendingEditCount, showToast]);

  const resubmitRowsStable = useCallback(
    async (
      rowIds: string[],
      confirmations: SampleSheetSubmissionConfirmationOptions = {},
    ): Promise<SampleSheetSubmissionAttemptOutcome> => {
      const result = await resubmitSampleEdits(rowIds, confirmations);
      if (result.status === 'requires_metadata_limitations_confirmation') {
        if (confirmations.metadataLimitationsConfirmationToken === result.confirmationToken) {
          throw new Error('Metadata limitations confirmation failed to proceed. Please retry.');
        }
        setMetadataLimitationsAcknowledged(false);
        setPendingPartialVolumeConfirmation(null);
        setPendingMetadataLimitationsConfirmation({
          source: 'resubmit',
          rowIds,
          payload: result,
        });
        return 'metadata_limitations_confirmation';
      }
      if (result.status === 'requires_partial_volume_confirmation') {
        if (confirmations.confirmProceedWithPartialVolumeNormalisation) {
          throw new Error('Partial volume confirmation failed to proceed. Please retry.');
        }
        setMetadataLimitationsAcknowledged(false);
        setPendingMetadataLimitationsConfirmation(null);
        setPendingPartialVolumeConfirmation({
          source: 'resubmit',
          rowIds,
          payload: result,
          confirmations,
        });
        return 'partial_volume_confirmation';
      }
      setPendingMetadataLimitationsConfirmation(null);
      setPendingPartialVolumeConfirmation(null);
      showToast({
        status: 'success',
        title: 'Edits submitted',
        description: `${rowIds.length} row${rowIds.length === 1 ? '' : 's'} updated.`,
      });
      void refetchSamplingPointChoices();
      if (resolvedLabWorkbookId) {
        refreshLabWorkbookDependentQueries(resolvedLabWorkbookId);
      }
      return 'submitted';
    },
    [
      refreshLabWorkbookDependentQueries,
      refetchSamplingPointChoices,
      resolvedLabWorkbookId,
      resubmitSampleEdits,
      showToast,
    ],
  );

  const handleResubmitSubmittedEdits = useCallback(() => {
    if (!canEditSubmittedSamples) {
      return;
    }
    if (missingGenePanel) {
      showToast({
        status: 'warning',
        title: 'Select genes before submitting edits',
        description: GENE_SELECTION_REQUIRED_MESSAGE,
      });
      return;
    }
    if (invalidGenePanel) {
      showToast({
        status: 'warning',
        title: 'Gene panel not ready',
        description: 'Verify the linked gene panel is valid before submitting edits.',
      });
      return;
    }
    if (pendingEditCount > 0) {
      showToast({
        status: 'warning',
        title: 'Wait for sync',
        description: 'Wait for pending edits to sync before submitting edits.',
      });
      return;
    }
    if (allEditableSubmittedRowIds.length === 0) {
      showToast({
        status: 'info',
        title: 'No editable rows',
        description: 'No editable submitted rows available.',
      });
      return;
    }

    const api = gridRef.current?.api ?? null;
    pendingSelectionRestoreRef.current = null;
    api?.clearCellSelection();
    rowSelectionAnchorIndexRef.current = null;
    setRangeSummary(null);
    setSelectedRowIds(prev => (prev.size === 0 ? prev : new Set()));

    setIsResubmittingSubmittedEdits(true);
    void resubmitRowsStable(allEditableSubmittedRowIds)
      .catch(error => {
        const description = error instanceof Error ? error.message : 'Unknown error';
        showToast({ status: 'error', title: 'Failed to submit edits', description });
      })
      .finally(() => {
        setIsResubmittingSubmittedEdits(false);
      });
  }, [
    allEditableSubmittedRowIds,
    canEditSubmittedSamples,
    invalidGenePanel,
    missingGenePanel,
    pendingEditCount,
    resubmitRowsStable,
    showToast,
  ]);

  const handleProceedWithPartialVolumeConfirmation = useCallback(async () => {
    if (!pendingPartialVolumeConfirmation) {
      return;
    }
    const { source, rowIds, confirmations } = pendingPartialVolumeConfirmation;
    const confirmedOptions = {
      ...confirmations,
      confirmProceedWithPartialVolumeNormalisation: true,
    };
    if (source === 'submit') {
      setIsSubmitting(true);
      try {
        await submitRowsStable(rowIds, confirmedOptions);
      } catch (error) {
        const description = error instanceof Error ? error.message : 'Unknown error';
        showToast({ status: 'error', title: 'Failed to submit sample sheet', description });
        throw error;
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setIsResubmittingSubmittedEdits(true);
    try {
      await resubmitRowsStable(rowIds, confirmedOptions);
    } catch (error) {
      const description = error instanceof Error ? error.message : 'Unknown error';
      showToast({ status: 'error', title: 'Failed to submit edits', description });
      throw error;
    } finally {
      setIsResubmittingSubmittedEdits(false);
    }
  }, [pendingPartialVolumeConfirmation, resubmitRowsStable, showToast, submitRowsStable]);

  const handleProceedWithMetadataLimitations = useCallback(async () => {
    if (!pendingMetadataLimitationsConfirmation || !metadataLimitationsAcknowledged) {
      return 'metadata_limitations_confirmation' as const;
    }
    const { source, rowIds, payload, confirmations } = pendingMetadataLimitationsConfirmation;
    const confirmedOptions: SampleSheetSubmissionConfirmationOptions = {
      ...confirmations,
      metadataLimitationsConfirmationToken: payload.confirmationToken,
      confirmProceedWithPartialVolumeNormalisation: payload.partialVolumeNormalisation != null,
    };
    if (source === 'submit') {
      setIsSubmitting(true);
      try {
        return await submitRowsStable(rowIds, confirmedOptions);
      } catch (error) {
        const description = error instanceof Error ? error.message : 'Unknown error';
        showToast({ status: 'error', title: 'Failed to submit sample sheet', description });
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    }

    setIsResubmittingSubmittedEdits(true);
    try {
      return await resubmitRowsStable(rowIds, confirmedOptions);
    } catch (error) {
      const description = error instanceof Error ? error.message : 'Unknown error';
      showToast({ status: 'error', title: 'Failed to submit edits', description });
      throw error;
    } finally {
      setIsResubmittingSubmittedEdits(false);
    }
  }, [
    metadataLimitationsAcknowledged,
    pendingMetadataLimitationsConfirmation,
    resubmitRowsStable,
    showToast,
    submitRowsStable,
  ]);

  const handleRowViewModeChangeStable = useCallback(
    (nextMode: SampleSheetRowViewMode) => {
      if (nextMode === adminRowViewMode) {
        return;
      }

      // Only admins can have blocking edits
      if (canEditSubmittedSamples && nextMode === 'draft' && hasBlockingSubmittedEdits) {
        showToast({
          status: 'warning',
          title: 'Cannot switch to drafts',
          description: 'Cancel or submit your edits before switching to drafts.',
        });
        return;
      }

      const api = gridRef.current?.api ?? null;
      pendingSelectionRestoreRef.current = null;
      api?.clearCellSelection();
      rowSelectionAnchorIndexRef.current = null;
      setRangeSummary(null);
      setSelectedRowIds(prev => (prev.size === 0 ? prev : new Set()));

      if (nextMode === 'draft') {
        setAdminRowViewMode('draft');
        return;
      }

      if (submittedSampleRowCount === 0) {
        return;
      }

      // Viewing approved samples must stay read-only. Starting an edit session materializes separate submitted-row
      // models; doing that for a fully approved legacy workbook briefly replaces hydrated sample values with blanks.
      if (!canEditSubmittedSamples || !hasSubmittedRowsEligibleForEditSession) {
        setAdminRowViewMode('submitted');
        return;
      }

      // Admins: start edit session when switching to submitted mode
      if (hasSubmittedEditsInProgress) {
        setAdminRowViewMode('submitted');
        return;
      }

      setIsEnteringSubmittedEditMode(true);
      void beginSampleEdits()
        .then(() => {
          setAdminRowViewMode('submitted');
        })
        .catch(error => {
          const description = error instanceof Error ? error.message : 'Unknown error';
          showToast({
            status: 'error',
            title: 'Failed to load submitted samples for editing',
            description,
          });
        })
        .finally(() => {
          setIsEnteringSubmittedEditMode(false);
        });
    },
    [
      adminRowViewMode,
      beginSampleEdits,
      canEditSubmittedSamples,
      hasBlockingSubmittedEdits,
      hasSubmittedEditsInProgress,
      hasSubmittedRowsEligibleForEditSession,
      showToast,
      submittedSampleRowCount,
    ],
  );

  const updateRangeSummary = useCallback(
    (ranges: readonly CellRange[] | null | undefined) => {
      if (!ranges || ranges.length === 0) {
        setRangeSummary(null);
        setSelectedRowIds(prev => (prev.size === 0 ? prev : new Set<string>()));
        if (samplingPointHighlightByRowId.size > 0) {
          setSamplingPointHighlightByRowId(new Map());
        }
        return;
      }
      const api = gridRef.current?.api ?? null;
      const visibleSelectableColumns = api
        ? getVisibleSelectableColumnIds(api, { excludedColIds: SAMPLE_SHEET_EXCLUDED_COL_IDS })
        : [];
      const uniqueRowIds = new Set<string>();
      const uniqueColumnIds = new Set<string>();
      const selectedColumnsByRowId = new Map<string, Set<string>>();
      let cellCount = 0;

      ranges.forEach((range: CellRange) => {
        const columns = range.columns;
        if (columns.length === 0) {
          return;
        }
        const columnIds = columns
          .map(column => {
            const columnFieldFromDef = column.getColDef().field;
            if (typeof columnFieldFromDef === 'string' && columnFieldFromDef.length > 0) {
              return columnFieldFromDef;
            }
            return column.getColId();
          })
          .filter((field): field is string => Boolean(field) && field !== ROW_INDEX_COL_ID);

        const rowIndexOnlySelection =
          columnIds.length === 0 &&
          columns.every(column => column.getColId() === ROW_INDEX_COL_ID) &&
          visibleSelectableColumns.length > 0;

        if (columnIds.length === 0 && !rowIndexOnlySelection) {
          return;
        }

        const effectiveColumnIds = rowIndexOnlySelection ? visibleSelectableColumns : columnIds;

        effectiveColumnIds.forEach(field => {
          uniqueColumnIds.add(field);
        });

        const startPosition = range.startRow;
        const endPosition = range.endRow ?? range.startRow;
        if (!startPosition || !endPosition) {
          return;
        }
        const startIndex = startPosition.rowIndex;
        const endIndex = endPosition.rowIndex;
        const rowsInRange = Math.abs(endIndex - startIndex) + 1;
        cellCount += rowsInRange * effectiveColumnIds.length;

        if (api && startPosition.rowPinned == null && endPosition.rowPinned == null) {
          for (let index = Math.min(startIndex, endIndex); index <= Math.max(startIndex, endIndex); index += 1) {
            const rowNode = api.getDisplayedRowAtIndex(index);
            const dataRowId = rowNode?.data?.rowId;
            const rowId = typeof dataRowId === 'string' && dataRowId.length > 0 ? dataRowId : rowNode?.id;
            if (typeof rowId !== 'string' || rowId.length === 0) {
              continue;
            }
            uniqueRowIds.add(rowId);
            if (effectiveColumnIds.length === 0) {
              continue;
            }
            const existing = selectedColumnsByRowId.get(rowId) ?? new Set<string>();
            effectiveColumnIds.forEach(field => existing.add(field));
            selectedColumnsByRowId.set(rowId, existing);
          }
        } else {
          for (let index = Math.min(startIndex, endIndex); index <= Math.max(startIndex, endIndex); index += 1) {
            uniqueRowIds.add(String(index));
          }
        }
      });

      if (cellCount === 0) {
        setRangeSummary(null);
        setSelectedRowIds(prev => (prev.size === 0 ? prev : new Set<string>()));
        if (samplingPointHighlightByRowId.size > 0) {
          setSamplingPointHighlightByRowId(new Map());
        }
        return;
      }

      setRangeSummary({ rowCount: uniqueRowIds.size, columnCount: uniqueColumnIds.size, cellCount });

      const hasSamplingPointColumns = Array.from(uniqueColumnIds).some(field =>
        samplingPointFieldSet.has(field as keyof SampleSheetRowDraft),
      );
      const hasNonSamplingPointColumns = Array.from(uniqueColumnIds).some(
        field => !samplingPointFieldSet.has(field as keyof SampleSheetRowDraft),
      );

      const nextHighlight = new Map<string, 'existing' | 'new'>();
      if (hasSamplingPointColumns && !hasNonSamplingPointColumns) {
        const rowIds = Array.from(uniqueRowIds);
        if (rowIds.length > 0) {
          const [firstRowId, ...remainingRowIds] = rowIds;
          const firstRow = rowsById.get(firstRowId);
          const firstKey =
            firstRow != null
              ? samplingPointKeyDetails.keyByRowId.get(firstRowId) ?? buildSamplingPointUniqueKey(firstRow)
              : null;

          const candidateKey = firstKey;
          let selectionHasMissingKey = candidateKey == null || firstRow == null;
          let selectionHasMultipleKeys = false;

          if (!selectionHasMissingKey) {
            for (const rowId of remainingRowIds) {
              if (selectionHasMultipleKeys) {
                break;
              }
              const row = rowsById.get(rowId);
              if (!row) {
                selectionHasMissingKey = true;
                break;
              }
              const key = samplingPointKeyDetails.keyByRowId.get(rowId) ?? buildSamplingPointUniqueKey(row);
              if (key == null) {
                selectionHasMissingKey = true;
                break;
              }
              if (key !== candidateKey) {
                selectionHasMultipleKeys = true;
              }
            }
          }

          if (
            candidateKey &&
            !selectionHasMissingKey &&
            !selectionHasMultipleKeys &&
            (samplingPointKeyDetails.countByKey.get(candidateKey) ?? 0) > 1
          ) {
            samplingPointKeyDetails.keyByRowId.forEach((key, rowId) => {
              if (key !== candidateKey) {
                return;
              }
              const row = rowsById.get(rowId);
              if (!row) {
                return;
              }
              nextHighlight.set(rowId, row.samplingPointId != null ? 'existing' : 'new');
            });
          }
        }
      }

      if (!mapsAreEqual(nextHighlight, samplingPointHighlightByRowId)) {
        setSamplingPointHighlightByRowId(nextHighlight);
      }

      if (!api) {
        setSelectedRowIds(prev => (prev.size === 0 ? prev : new Set<string>()));
        return;
      }

      if (visibleSelectableColumns.length === 0) {
        setSelectedRowIds(prev => (prev.size === 0 ? prev : new Set<string>()));
        return;
      }

      const requiredColumns = new Set(visibleSelectableColumns);
      const fullySelected = new Set<string>();
      selectedColumnsByRowId.forEach((columnsForRow, rowId) => {
        if (columnsForRow.size < requiredColumns.size) {
          return;
        }
        for (const required of requiredColumns) {
          if (!columnsForRow.has(required)) {
            return;
          }
        }
        fullySelected.add(rowId);
      });

      setSelectedRowIds(prev => (setsAreEqual(prev, fullySelected) ? prev : fullySelected));
    },
    [rowsById, samplingPointFieldSet, samplingPointHighlightByRowId, samplingPointKeyDetails],
  );

  // Capture selection after read-only rows are hidden/shown
  const restoreSelectionSnapshot = useCallback(
    (api: GridApi<SampleSheetRowDraft>, snapshot: SelectionSnapshot) => {
      type AnyCol = { getColId(): string; isVisible(): boolean };
      const apiWithCols = api as GridApi<SampleSheetRowDraft> & { getColumns?: () => AnyCol[] };
      const visibleColumns = typeof apiWithCols.getColumns === 'function' ? apiWithCols.getColumns() ?? [] : [];
      const visibleColumnIdSet = new Set(
        visibleColumns
          .filter((column: AnyCol) => column.isVisible() && column.getColId() !== ROW_INDEX_COL_ID)
          .map((column: AnyCol) => column.getColId()),
      );

      const restoredRanges: Array<{ startIndex: number; endIndex: number; columns: string[] }> = [];

      snapshot.ranges.forEach(range => {
        const candidateRowIndices = range.rowIds
          .map(rowId => api.getRowNode(rowId))
          .filter((node): node is IRowNode<SampleSheetRowDraft> => Boolean(node))
          .filter(node => node.rowPinned == null && typeof node.rowIndex === 'number')
          .map(node => node.rowIndex as number)
          .sort((left, right) => left - right);

        if (candidateRowIndices.length === 0) {
          return;
        }

        const candidateColumns = range.columnIds.filter(columnId => visibleColumnIdSet.has(columnId));
        if (candidateColumns.length === 0) {
          return;
        }

        restoredRanges.push({
          startIndex: candidateRowIndices[0],
          endIndex: candidateRowIndices[candidateRowIndices.length - 1],
          columns: candidateColumns,
        });
      });

      if (restoredRanges.length === 0) {
        api.clearCellSelection();
        rowSelectionAnchorIndexRef.current = null;
        updateRangeSummary(api.getCellRanges());
        return;
      }

      api.clearCellSelection();
      restoredRanges.forEach(range => {
        api.addCellRange({ rowStartIndex: range.startIndex, rowEndIndex: range.endIndex, columns: range.columns });
      });

      let nextAnchorIndex: number | null = null;
      if (snapshot.anchorRowId) {
        const anchorNode = api.getRowNode(snapshot.anchorRowId);
        if (anchorNode && anchorNode.rowPinned == null && typeof anchorNode.rowIndex === 'number') {
          nextAnchorIndex = anchorNode.rowIndex;
        }
      }
      if (nextAnchorIndex == null) {
        nextAnchorIndex = restoredRanges[0]?.startIndex ?? null;
      }
      rowSelectionAnchorIndexRef.current = nextAnchorIndex;

      if (snapshot.focusedRowId && snapshot.focusedColumnId && visibleColumnIdSet.has(snapshot.focusedColumnId)) {
        const focusedNode = api.getRowNode(snapshot.focusedRowId);
        if (focusedNode && focusedNode.rowPinned == null && typeof focusedNode.rowIndex === 'number') {
          api.setFocusedCell(focusedNode.rowIndex, snapshot.focusedColumnId);
        }
      }

      updateRangeSummary(api.getCellRanges());
    },
    [updateRangeSummary],
  );
  const handleReadyToSubmitSummaryClick = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }

    type AnyCol = { getColId(): string; isVisible(): boolean };
    const apiWithCols = api as GridApi<SampleSheetRowDraft> & { getColumns?: () => AnyCol[] };
    const allColumns: AnyCol[] = typeof apiWithCols.getColumns === 'function' ? apiWithCols.getColumns() ?? [] : [];
    const visibleColumnIds = allColumns
      .filter((column: AnyCol) => column.isVisible() && column.getColId() !== ROW_INDEX_COL_ID)
      .map((column: AnyCol) => column.getColId());

    if (visibleColumnIds.length === 0) {
      return;
    }

    const readyRowIds = new Set(rowsReadyForSubmit.map(row => row.rowId));
    const displayedRowCount = api.getDisplayedRowCount();
    const rowIndexesToSelect = lodashRange(displayedRowCount)
      .map(index => {
        const rowNode = api.getDisplayedRowAtIndex(index);
        if (!rowNode || rowNode.rowPinned != null) {
          return null;
        }
        const dataRowId =
          typeof rowNode.data?.rowId === 'string' && rowNode.data.rowId.length > 0 ? rowNode.data.rowId : rowNode.id;
        if (!dataRowId || !readyRowIds.has(dataRowId)) {
          return null;
        }
        return index;
      })
      .filter((index): index is number => index != null);

    if (rowIndexesToSelect.length === 0) {
      showToast({
        status: 'info',
        title: 'Ready rows are hidden',
        description: 'No ready rows are currently visible. Adjust filters or row visibility and try again.',
      });
      return;
    }

    api.clearCellSelection();
    rowIndexesToSelect.forEach(index => {
      api.addCellRange({ rowStartIndex: index, rowEndIndex: index, columns: visibleColumnIds });
    });
    api.ensureIndexVisible(rowIndexesToSelect[0]);
    rowSelectionAnchorIndexRef.current = rowIndexesToSelect[0];
    updateRangeSummary(api.getCellRanges());
  }, [rowsReadyForSubmit, showToast, updateRangeSummary]);
  const handleSelectAllCells = useCallback(() => {
    const api = gridRef.current?.api ?? null;
    if (!api) {
      return;
    }

    const visibleColumnIds = getVisibleSelectableColumnIds(api, { excludedColIds: SAMPLE_SHEET_EXCLUDED_COL_IDS });
    if (visibleColumnIds.length === 0) {
      return;
    }

    type SelectableRow = { index: number; row: IRowNode<SampleSheetRowDraft> | undefined };
    const selectableRows: SelectableRow[] = lodashRange(api.getDisplayedRowCount())
      .map(index => ({ index, row: api.getDisplayedRowAtIndex(index) }))
      .filter(({ row }) => row?.rowPinned == null);

    if (selectableRows.length === 0) {
      return;
    }

    const firstSelectable = selectableRows[0].index;
    const lastNonEmptyRow = findLast(selectableRows, ({ row }) => {
      if (!row?.data) {
        return false;
      }
      return !isRowEmpty(row.data);
    });

    const lastSelectable = selectableRows[selectableRows.length - 1].index;
    const lastNonEmptyIndex = lastNonEmptyRow?.index ?? lastSelectable;
    const focusColumnId = visibleColumnIds[0];

    api.clearCellSelection();
    api.addCellRange({ rowStartIndex: firstSelectable, rowEndIndex: lastNonEmptyIndex, columns: visibleColumnIds });
    rowSelectionAnchorIndexRef.current = firstSelectable;
    api.setFocusedCell(firstSelectable, focusColumnId);
    api.ensureIndexVisible(firstSelectable);
    updateRangeSummary(api.getCellRanges());
  }, [updateRangeSummary]);
  const handleSelectAllCellsRef = useLatestRef(handleSelectAllCells);
  const handleSelectAllCellsStable = useCallback(() => {
    handleSelectAllCellsRef.current();
  }, [handleSelectAllCellsRef]);

  const columnDefs = useMemo(() => {
    const rowIndexCol: ColDef<SampleSheetRowDraft> = {
      colId: ROW_INDEX_COL_ID,
      headerName: '',
      pinned: 'left',
      width: 80,
      minWidth: 80,
      maxWidth: 100,
      resizable: false,
      sortable: false,
      filter: false,
      editable: false,
      lockVisible: true,
      suppressColumnsToolPanel: true,
      lockPinned: true,
      suppressNavigable: true,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      headerComponent: RowIndexHeaderComponent,
      headerComponentParams: { onSelectAll: handleSelectAllCellsStable },
      headerTooltip: 'Select all rows',

      headerClass: 'sample-sheet-row-index-header',
      cellClass: 'sample-sheet-row-index-cell sample-sheet-readonly-cell',
      valueGetter: params => {
        const row = params.data;
        if (!row) {
          return '';
        }
        return previewSampleLabelByRowIdRef.current.get(row.rowId) ?? '';
      },
      tooltipValueGetter: () => 'Click to select row; Backspace to clear',
    };
    return [rowIndexCol, ...builtColumnDefs];
  }, [builtColumnDefs, handleSelectAllCellsStable, previewSampleLabelByRowIdRef]);

  const rowSummarySegments = useMemo<readonly RowSummarySegment[]>(() => {
    const segments: RowSummarySegment[] = [];
    if (readyToSubmitCount > 0) {
      segments.push({
        key: 'ready',
        label: `${readyToSubmitCount} ready to submit`,
        onClick: handleReadyToSubmitSummaryClick,
      });
    }
    if (incompleteRowCount > 0) {
      segments.push({ key: 'incomplete', label: `${incompleteRowCount} invalid` });
    }
    return segments;
  }, [handleReadyToSubmitSummaryClick, incompleteRowCount, readyToSubmitCount]);

  const actionFooterDensity =
    useBreakpointValue<SmartChipActionFooterDensity>(
      { base: 'compact', md: 'condensed', lg2: 'full' },
      { fallback: 'full' },
    ) ?? 'full';

  const hasRowSummarySegments = rowSummarySegments.length > 0;
  const isFiltered = filteredRowCount != null;
  // Only show the row summary if there's something useful to display:
  // - filtered rows info, or
  // - additional segments (invalid, ready to submit, etc.)
  const shouldShowRowSummary = isFiltered || hasRowSummarySegments;

  const rowSummaryTitle = useMemo(() => {
    const segmentText = rowSummarySegments.map(segment => segment.label).join(', ');
    if (segmentText) {
      // When there are segments and not filtered, add "total" to clarify the row count
      const totalSuffix = isFiltered ? '' : ' total';
      return `${displayedRowSummary}${totalSuffix} (${segmentText})`;
    }
    return displayedRowSummary;
  }, [displayedRowSummary, isFiltered, rowSummarySegments]);

  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<SampleSheetRowDraft>) => {
      const columnField =
        typeof event.colDef.field === 'string' && event.colDef.field.length > 0
          ? event.colDef.field
          : event.column.getColId();
      const mutatedField = columnField as keyof SampleSheetRowDraft;
      const rowId = event.data.rowId;
      const cellKey = rowId ? createCellKey(rowId, String(mutatedField)) : null;

      const source = event.source;
      // AG Grid uses 'rangeSvc' for fill handle value changes.
      // 'fillHandle' edge-case is used when the dragged single cell is edited during a drag-fill operation.
      const isUserDriven = source === 'edit' || source === 'paste' || source === 'rangeSvc' || source === 'fillHandle';
      const isIgnoredManualChange = cellKey != null && ignoredHistoryCellsRef.current.has(cellKey);

      if (!isUserDriven || isIgnoredManualChange) {
        if (isIgnoredManualChange && cellKey) {
          ignoredHistoryCellsRef.current.delete(cellKey);
        }
        const passiveRanges = event.api.getCellRanges();
        if (passiveRanges) {
          updateRangeSummary(passiveRanges);
        }
        return;
      }

      const mutatedFields = new Set<keyof SampleSheetRowDraft>([mutatedField]);
      recordHistoryChange({
        rowId: event.data.rowId,
        field: mutatedField,
        previousValue: snapshotCellValue(event.oldValue ?? null),
        newValue: snapshotCellValue(event.newValue ?? null),
      });

      if (mutatedField === 'detailedSampleType' && event.source === 'edit') {
        const sampleTypeValue = event.data.sampleType;
        const sampleTypeEmpty =
          sampleTypeValue == null || (typeof sampleTypeValue === 'string' && sampleTypeValue.trim().length === 0);
        if (sampleTypeEmpty) {
          const subtype = toSamplingPointSubType(event.data.detailedSampleType);
          if (subtype) {
            const inferredSampleType = getParentType(subtype);
            const rowNode = event.node;
            if (event.data.sampleType !== inferredSampleType) {
              rowNode.setDataValue('sampleType', inferredSampleType);
            }
          }
        }
      }

      if (!isPastingRef.current) {
        queueRowUpdates(event.data, mutatedFields);
      }
      const activeRanges = event.api.getCellRanges();
      if (activeRanges) {
        updateRangeSummary(activeRanges);
      }
    },
    [queueRowUpdates, recordHistoryChange, updateRangeSummary],
  );

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }
    const ranges = api.getCellRanges();
    if (!ranges || ranges.length === 0) {
      if (samplingPointHighlightByRowId.size > 0) {
        setSamplingPointHighlightByRowId(new Map());
      }
      return;
    }
    updateRangeSummary(ranges);
  }, [samplingPointHighlightByRowId.size, samplingPointKeyDetails, updateRangeSummary]);

  const handleCellSelectionChanged = useCallback(
    (event: CellSelectionChangedEvent<SampleSheetRowDraft>) => {
      updateRangeSummary(event.api.getCellRanges());
    },
    [updateRangeSummary],
  );

  const handleFilterChanged = useCallback((event: FilterChangedEvent<SampleSheetRowDraft>) => {
    const filterModel = event.api.getFilterModel();
    const nextActive = Object.keys(filterModel).sort((a, b) => a.localeCompare(b));
    const displayedCount = nextActive.length > 0 ? event.api.getDisplayedRowCount() : null;
    setActiveFilterColIds(prev => (arraysAreEqual(prev, nextActive) ? prev : nextActive));
    setFilteredRowCount(displayedCount);
  }, []);

  const handleBodyScroll = useCallback(() => {
    maybeEnsureRowCapacity('scroll');
  }, [maybeEnsureRowCapacity]);

  const handleFirstDataRendered = useCallback(() => {
    // No-op: grid height is now auto-sized
  }, []);

  const handleModelUpdated = useCallback(
    (event: ModelUpdatedEvent<SampleSheetRowDraft>) => {
      const pendingSnapshot = pendingSelectionRestoreRef.current;
      if (pendingSnapshot) {
        pendingSelectionRestoreRef.current = null;
        restoreSelectionSnapshot(event.api, pendingSnapshot);
      }
    },
    [restoreSelectionSnapshot],
  );

  const handleGridSizeChanged = useCallback(() => {
    // No-op: grid height is now auto-sized
  }, []);

  useEffect(() => {
    const container = gridContainerRef.current;
    const api = gridRef.current?.api;
    if (!container || !api) {
      return;
    }
    const headerRoot = container.querySelector('.ag-header');
    if (!headerRoot) {
      return;
    }

    const selectColumnById = (colId: string) => {
      if (!colId || colId === ROW_INDEX_COL_ID) {
        return;
      }
      const rowCount = api.getDisplayedRowCount();
      if (rowCount <= 0) {
        return;
      }
      const previousFocus = api.getFocusedCell();
      api.clearCellSelection();
      api.addCellRange({ rowStartIndex: 0, rowEndIndex: rowCount - 1, columns: [colId] });
      let rowIndexToFocus = previousFocus?.rowIndex ?? rowSelectionAnchorIndexRef.current ?? null;
      if (rowIndexToFocus == null || rowIndexToFocus < 0 || rowIndexToFocus >= rowCount) {
        const firstRenderedNode = api
          .getRenderedNodes()
          .find(node => node.rowPinned == null && node.rowIndex != null && node.rowIndex >= 0);
        rowIndexToFocus = firstRenderedNode?.rowIndex ?? rowIndexToFocus;
      }
      if (rowIndexToFocus == null || rowIndexToFocus < 0 || rowIndexToFocus >= rowCount) {
        rowIndexToFocus = 0;
      }
      api.setFocusedCell(rowIndexToFocus, colId);
    };

    const clickHandler = (ev: Event) => {
      const target = ev.target as HTMLElement | null;
      if (!target) {
        return;
      }
      const menuButton = target.closest('.ag-header-cell-menu-button, .ag-header-menu-button');
      if (menuButton) {
        return;
      }
      const cell = target.closest('.ag-header-cell');
      if (!(cell instanceof HTMLElement)) {
        return;
      }
      const colId = cell.getAttribute('col-id');
      if (colId) {
        ev.preventDefault();
        ev.stopPropagation();
        selectColumnById(colId);
      }
    };

    const updateTitles = () => {
      headerRoot.querySelectorAll<HTMLElement>('.ag-header-cell').forEach(cell => {
        const colId = cell.getAttribute('col-id');
        if (!colId || colId === ROW_INDEX_COL_ID) {
          return;
        }
        const nextTitle = 'Click to select whole column; Backspace to clear';
        if (cell.title !== nextTitle) {
          cell.title = nextTitle;
        }
        if (cell.style.cursor !== 'pointer') {
          cell.style.cursor = 'pointer';
        }
      });
    };

    updateTitles();
    headerRoot.addEventListener('click', clickHandler, true);
    const mo = new MutationObserver(updateTitles);
    mo.observe(headerRoot, { childList: true, subtree: true });
    return () => {
      headerRoot.removeEventListener('click', clickHandler, true);
      mo.disconnect();
    };
  }, []);

  const copyRange = useCallback(
    (includeHeaders: boolean) => {
      const api = gridRef.current?.api;
      if (!api) {
        return false;
      }
      const ranges = api.getCellRanges();
      if (!ranges || ranges.length === 0) {
        showToast({
          status: 'info',
          title: 'Select cells to copy',
          description: 'Highlight one or more cells before copying.',
        });
        return false;
      }
      api.copySelectedRangeToClipboard({ includeHeaders });
      return true;
    },
    [showToast],
  );

  const clearSelectedCells = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }
    const ranges = api.getCellRanges();
    if (!ranges || ranges.length === 0) {
      showToast({
        status: 'info',
        title: 'Select cells to clear',
        description: 'Highlight one or more editable cells before clearing.',
      });
      return;
    }

    const selectedColumnFieldIds = new Set<string>();
    const selectedRowIdsForClear = new Set<string>();
    const blockedSubmittedSamplingPointTargets: Array<{
      rowId: string;
      rowIndex: number;
      colId: string;
      field: keyof SampleSheetRowDraft;
    }> = [];

    const columnsByRowId = new Map<string, Set<keyof SampleSheetRowDraft>>();
    ranges.forEach((range: CellRange) => {
      const columns = range.columns;
      if (columns.length === 0) {
        return;
      }
      columns.forEach(column => {
        const columnFieldFromDef = column.getColDef().field;
        const fieldId =
          typeof columnFieldFromDef === 'string' && columnFieldFromDef.length > 0
            ? columnFieldFromDef
            : column.getColId();
        if (fieldId) {
          selectedColumnFieldIds.add(fieldId);
        }
      });
      const startRow = range.startRow;
      const endRow = range.endRow ?? range.startRow;
      if (!startRow || !endRow) {
        return;
      }
      if (startRow.rowPinned != null || endRow.rowPinned != null) {
        return;
      }
      const startIndex = Math.min(startRow.rowIndex, endRow.rowIndex);
      const endIndex = Math.max(startRow.rowIndex, endRow.rowIndex);
      for (let rowIndex = startIndex; rowIndex <= endIndex; rowIndex += 1) {
        const rowNode = api.getDisplayedRowAtIndex(rowIndex);
        if (!rowNode?.data) {
          continue;
        }
        const rowData = rowNode.data;
        selectedRowIdsForClear.add(rowData.rowId);
        if (rowData.isReadOnly) {
          continue;
        }
        columns.forEach(column => {
          const columnFieldFromDef = column.getColDef().field;
          const field =
            typeof columnFieldFromDef === 'string' && columnFieldFromDef.length > 0
              ? columnFieldFromDef
              : column.getColId();
          if (!field) {
            return;
          }
          const typedField = field as keyof SampleSheetRowDraft;
          if (!column.isCellEditable(rowNode)) {
            if (isSubmittedSampleLikeRow(rowData) && samplingPointFieldSet.has(typedField)) {
              blockedSubmittedSamplingPointTargets.push({
                rowId: rowData.rowId,
                rowIndex,
                colId: column.getColId(),
                field: typedField,
              });
            }
            return;
          }
          let columnsForRow = columnsByRowId.get(rowData.rowId);
          if (!columnsForRow) {
            columnsForRow = new Set<keyof SampleSheetRowDraft>();
            columnsByRowId.set(rowData.rowId, columnsForRow);
          }
          columnsForRow.add(typedField);
        });
      }
    });

    if (blockedSubmittedSamplingPointTargets.length > 0) {
      const rowIds = Array.from(new Set(blockedSubmittedSamplingPointTargets.map(target => target.rowId)));
      const allSelectedColumnsAreSamplingPointFields =
        selectedColumnFieldIds.size > 0 &&
        Array.from(selectedColumnFieldIds).every(fieldId =>
          samplingPointFieldSet.has(fieldId as keyof SampleSheetRowDraft),
        );

      if (rowIds.length === 1 && allSelectedColumnsAreSamplingPointFields) {
        const focusedCell = api.getFocusedCell();
        const resolvePreferredTarget = () => {
          if (focusedCell && focusedCell.rowPinned == null) {
            const focusedNode = api.getDisplayedRowAtIndex(focusedCell.rowIndex);
            const focusedRowId = focusedNode?.data?.rowId;
            const focusedColId = focusedCell.column.getColId();
            if (focusedRowId && focusedColId) {
              const exactMatch = blockedSubmittedSamplingPointTargets.find(
                target => target.rowId === focusedRowId && target.colId === focusedColId,
              );
              if (exactMatch) {
                return exactMatch;
              }
              const rowMatch = blockedSubmittedSamplingPointTargets.find(target => target.rowId === focusedRowId);
              if (rowMatch) {
                return rowMatch;
              }
            }
          }
          return blockedSubmittedSamplingPointTargets[0];
        };

        const preferredTarget = resolvePreferredTarget();
        void beginSubmittedSamplingPointEdit({
          api,
          rowId: preferredTarget.rowId,
          rowIndex: preferredTarget.rowIndex,
          colId: preferredTarget.colId,
          field: preferredTarget.field,
        });
        return;
      }

      showToast({
        status: 'warning',
        title: 'Clear not supported for shared sampling points',
        description:
          selectedRowIdsForClear.size > 1
            ? 'This selection includes sampling point fields for submitted samples. Clearing across multiple rows is not supported when the sampling point is shared. Edit one row at a time (double click a sampling point cell to clear first).'
            : 'This selection includes sampling point fields for submitted samples. Clearing a mixed selection is not supported when the sampling point is shared. Clear the sampling point for one row first (double click a sampling point cell), then clear other fields.',
      });
      return;
    }

    if (columnsByRowId.size === 0) {
      showToast({
        status: 'info',
        title: 'Nothing to clear',
        description: 'The current selection does not include editable cells.',
      });
      return;
    }

    const aggregatedHistoryChanges = new Map<string, CellHistoryChange>();
    const addHistoryChange = (change: CellHistoryChange) => {
      const key = `${change.rowId}::${String(change.field)}`;
      if (aggregatedHistoryChanges.has(key)) {
        return;
      }
      aggregatedHistoryChanges.set(key, change);
    };

    columnsByRowId.forEach((fields, rowId) => {
      const row = rowsById.get(rowId);
      if (!row) {
        return;
      }
      const updatedRow = cloneRow(row);
      const mutatedFields = new Set<keyof SampleSheetRowDraft>();
      fields.forEach(field => {
        mutatedFields.add(field);
        const previousValue = snapshotCellValue(row[field] ?? null);
        if (clearDraftField(updatedRow, field)) {
          addHistoryChange({
            rowId,
            field,
            previousValue,
            newValue: snapshotCellValue(updatedRow[field] ?? null),
          });
        }
      });
      if (mutatedFields.size > 0) {
        const captureOpsForHistory = (ops: QueuePatchOp[]) => {
          ops.forEach(op => {
            addHistoryChange({
              rowId: op.rowId,
              field: op.field as keyof SampleSheetRowDraft,
              previousValue: snapshotCellValue(op.previousValue ?? null),
              newValue: snapshotCellValue(op.value ?? null),
            });
          });
        };
        suppressRowMutationHistoryRef.current = true;
        try {
          queueRowUpdates(updatedRow, mutatedFields, { onOps: captureOpsForHistory });
        } finally {
          suppressRowMutationHistoryRef.current = false;
        }
      }
    });
    const mergedHistoryChanges = Array.from(aggregatedHistoryChanges.values());
    if (mergedHistoryChanges.length > 0) {
      recordExplicitHistoryAction('edit', mergedHistoryChanges);
    }
  }, [
    beginSubmittedSamplingPointEdit,
    queueRowUpdates,
    recordExplicitHistoryAction,
    rowsById,
    samplingPointFieldSet,
    showToast,
  ]);

  const handleCellKeyDown = useCallback(
    (cellEvent: CellKeyDownEvent<SampleSheetRowDraft>) => {
      const keyboardEvent = cellEvent.event;
      if (!(keyboardEvent instanceof KeyboardEvent)) {
        return;
      }
      if (keyboardEvent.defaultPrevented) {
        return;
      }

      const api = cellEvent.api;
      const focusedRowIndex = cellEvent.rowIndex ?? null;
      const focusedColId = cellEvent.column.getColId();
      const tryClearSelection = () => {
        const editingCount = api.getEditingCells().length;
        if (editingCount > 0) {
          return false;
        }
        clearSelectedCells();
        return true;
      };

      const selectFocusedRow = () => {
        if (focusedRowIndex == null) {
          return false;
        }
        type AnyCol = { getColId(): string; isVisible(): boolean };
        const apiWithCols = api as GridApi<SampleSheetRowDraft> & { getColumns?: () => AnyCol[] };
        const allCols: AnyCol[] = typeof apiWithCols.getColumns === 'function' ? apiWithCols.getColumns() ?? [] : [];
        const visibleColIds = allCols.filter((c: AnyCol) => c.isVisible()).map((c: AnyCol) => c.getColId());
        if (visibleColIds.length === 0) {
          return false;
        }
        // Respect Shift to extend selection from anchor
        let startIndex = focusedRowIndex;
        let endIndex = focusedRowIndex;
        if (keyboardEvent.shiftKey && rowSelectionAnchorIndexRef.current != null) {
          startIndex = Math.min(rowSelectionAnchorIndexRef.current, focusedRowIndex);
          endIndex = Math.max(rowSelectionAnchorIndexRef.current, focusedRowIndex);
        } else {
          rowSelectionAnchorIndexRef.current = focusedRowIndex;
        }
        api.clearCellSelection();
        api.addCellRange({ rowStartIndex: startIndex, rowEndIndex: endIndex, columns: visibleColIds });
        return true;
      };

      const selectFocusedColumn = () => {
        api.clearCellSelection();
        const rowCount = api.getDisplayedRowCount();
        if (rowCount <= 0) {
          return false;
        }
        api.addCellRange({ rowStartIndex: 0, rowEndIndex: rowCount - 1, columns: [focusedColId] });
        return true;
      };

      const moveFocusBy = (rowDelta: number, colDelta: number): boolean =>
        moveFocusRelative(api, focusedColId, focusedRowIndex, rowDelta, colDelta, {
          excludedColIds: SAMPLE_SHEET_EXCLUDED_COL_IDS,
        });

      const editingCells = api.getEditingCells();
      const isEditingFocusedCell =
        editingCells.length > 0 &&
        editingCells.some(
          position =>
            position.rowPinned == null &&
            position.rowIndex === focusedRowIndex &&
            position.column?.getColId() === focusedColId,
        );

      const isSelectAllShortcut =
        keyboardEvent.key.toLowerCase() === 'a' &&
        !keyboardEvent.shiftKey &&
        !keyboardEvent.altKey &&
        (keyboardEvent.ctrlKey || keyboardEvent.metaKey);

      if (isEditingFocusedCell) {
        if (keyboardEvent.key === 'Enter') {
          keyboardEvent.preventDefault();
          keyboardEvent.stopPropagation();
          api.stopEditing();
          if (!moveFocusBy(keyboardEvent.shiftKey ? -1 : 1, 0)) {
            api.setFocusedCell(focusedRowIndex ?? 0, focusedColId);
          }
          return;
        }
        const arrowMovements: Partial<Record<string, { rowDelta: number; colDelta: number }>> = {
          ArrowDown: { rowDelta: 1, colDelta: 0 },
          ArrowUp: { rowDelta: -1, colDelta: 0 },
        };
        const movement = arrowMovements[keyboardEvent.key];
        if (movement) {
          keyboardEvent.preventDefault();
          keyboardEvent.stopPropagation();
          api.stopEditing();
          if (!moveFocusBy(movement.rowDelta, movement.colDelta)) {
            api.setFocusedCell(focusedRowIndex ?? 0, focusedColId);
          }
          return;
        }
      }

      if (keyboardEvent.key === 'Enter') {
        keyboardEvent.preventDefault();
        keyboardEvent.stopPropagation();
        if (!moveFocusBy(keyboardEvent.shiftKey ? -1 : 1, 0)) {
          api.setFocusedCell(focusedRowIndex ?? 0, focusedColId);
        }
        return;
      }

      if (isEditingFocusedCell && isSelectAllShortcut) {
        return;
      }

      if (isSelectAllShortcut) {
        keyboardEvent.preventDefault();
        keyboardEvent.stopPropagation();
        handleSelectAllCells();
        return;
      }

      const isCutShortcut =
        !keyboardEvent.shiftKey &&
        !keyboardEvent.altKey &&
        (keyboardEvent.ctrlKey || keyboardEvent.metaKey) &&
        keyboardEvent.key.toLowerCase() === 'x';
      if (!isEditingFocusedCell && isCutShortcut) {
        keyboardEvent.preventDefault();
        keyboardEvent.stopPropagation();
        const copied = copyRange(false);
        if (copied) {
          clearSelectedCells();
        }
        return;
      }

      // Backspace/Delete → clear selected cells
      if (keyboardEvent.key === 'Backspace' || keyboardEvent.key === 'Delete') {
        if (tryClearSelection()) {
          keyboardEvent.preventDefault();
          keyboardEvent.stopPropagation();
        }
        return;
      }

      // Shift+Space → select whole row
      if (keyboardEvent.key === ' ' && keyboardEvent.shiftKey) {
        if (selectFocusedRow()) {
          keyboardEvent.preventDefault();
          keyboardEvent.stopPropagation();
        }
        return;
      }

      // Ctrl/Meta+Space → select whole column
      if (keyboardEvent.key === ' ' && (keyboardEvent.ctrlKey || keyboardEvent.metaKey)) {
        if (selectFocusedColumn()) {
          keyboardEvent.preventDefault();
          keyboardEvent.stopPropagation();
        }
        return;
      }

      const node = cellEvent.node;
      const row = node.data ?? null;
      const columnFieldFromDef = cellEvent.column.getColDef().field;
      const field =
        typeof columnFieldFromDef === 'string' && columnFieldFromDef.length > 0 ? columnFieldFromDef : focusedColId;
      const typedField = field as keyof SampleSheetRowDraft;

      const maybeBeginBlockedSubmittedSamplingPointEdit = (eventKey?: string): boolean => {
        if (!row || !isSubmittedSampleLikeRow(row) || row.isReadOnly || !samplingPointFieldSet.has(typedField)) {
          return false;
        }
        if (cellEvent.column.isCellEditable(node)) {
          return false;
        }
        keyboardEvent.preventDefault();
        keyboardEvent.stopPropagation();
        void beginSubmittedSamplingPointEdit({
          api,
          rowId: row.rowId,
          rowIndex: focusedRowIndex,
          colId: focusedColId,
          field: typedField,
          ...(eventKey ? { eventKey } : {}),
        });
        return true;
      };

      if (keyboardEvent.key === 'F2') {
        maybeBeginBlockedSubmittedSamplingPointEdit();
        return;
      }

      // Start edit on printable keys without modifiers
      if (keyboardEvent.altKey || keyboardEvent.ctrlKey || keyboardEvent.metaKey) {
        return;
      }
      if (keyboardEvent.key.length !== 1) {
        return;
      }
      if (api.getEditingCells().length > 0) {
        return;
      }
      if (focusedRowIndex == null) {
        return;
      }

      if (maybeBeginBlockedSubmittedSamplingPointEdit(keyboardEvent.key)) {
        return;
      }

      api.startEditingCell({
        rowIndex: focusedRowIndex,
        colKey: focusedColId,
        eventKey: keyboardEvent.key,
      });
      keyboardEvent.preventDefault();
      keyboardEvent.stopPropagation();
    },
    [beginSubmittedSamplingPointEdit, clearSelectedCells, copyRange, handleSelectAllCells, samplingPointFieldSet],
  );

  const getContextMenuItems = useCallback(
    (
      contextMenuParams: GetContextMenuItemsParams<SampleSheetRowDraft>,
    ): Array<DefaultMenuItem | MenuItemDef<SampleSheetRowDraft>> => {
      const defaultItems = contextMenuParams.defaultItems ?? [];
      const filteredDefault = defaultItems.filter(
        (item: DefaultMenuItem | MenuItemDef<SampleSheetRowDraft>) => item !== 'copy' && item !== 'copyWithHeaders',
      );
      const selectionItems: Array<DefaultMenuItem | MenuItemDef<SampleSheetRowDraft>> = [
        {
          name: 'Select Entire Row',
          action: () => {
            const api = gridRef.current?.api;
            const rowIndex = contextMenuParams.node?.rowIndex ?? null;
            if (!api || rowIndex == null) {
              return;
            }
            type AnyCol = { getColId(): string; isVisible(): boolean };
            const apiWithCols = api as GridApi<SampleSheetRowDraft> & { getColumns?: () => AnyCol[] };
            const allCols: AnyCol[] =
              typeof apiWithCols.getColumns === 'function' ? apiWithCols.getColumns() ?? [] : [];
            const visibleColIds = allCols.filter((c: AnyCol) => c.isVisible()).map((c: AnyCol) => c.getColId());
            api.clearCellSelection();
            api.addCellRange({ rowStartIndex: rowIndex, rowEndIndex: rowIndex, columns: visibleColIds });
          },
          shortcut: 'Shift+Space',
        },
        {
          name: 'Select Entire Column',
          action: () => {
            const api = gridRef.current?.api;
            const colId = contextMenuParams.column?.getColId();
            if (!api || !colId) {
              return;
            }
            api.clearCellSelection();
            const rowCount = api.getDisplayedRowCount();
            if (rowCount > 0) {
              api.addCellRange({ rowStartIndex: 0, rowEndIndex: rowCount - 1, columns: [colId] });
            }
          },
          shortcut: isMacPlatform ? '⌘+Space' : 'Ctrl+Space',
        },
        ...(rangeSummary
          ? ([
              {
                name: 'Copy Range',
                action: () => {
                  copyRange(false);
                },
                shortcut: isMacPlatform ? '⌘C' : 'Ctrl+C',
              },
              {
                name: 'Copy Range with Headers',
                action: () => {
                  copyRange(true);
                },
              },
              {
                name: 'Clear Selected Cells',
                action: () => {
                  clearSelectedCells();
                },
              },
              'separator' as DefaultMenuItem,
            ] as Array<DefaultMenuItem | MenuItemDef<SampleSheetRowDraft>>)
          : ([] as Array<DefaultMenuItem | MenuItemDef<SampleSheetRowDraft>>)),
      ];
      return [...selectionItems, ...filteredDefault];
    },
    [clearSelectedCells, copyRange, isMacPlatform, rangeSummary],
  );

  if (invalidOrganisationId) {
    return (
      <div style={{ marginBottom: theme.spacing[16] }}>
        <SmartChipSection>
          <VStack align="stretch" spacing={4} width="100%">
            <Text color="red.600">
              Provide a valid lab workbook id in the URL (e.g. /lab/00000000-0000-0000-0000-000000000000/sample-spec).
            </Text>
          </VStack>
        </SmartChipSection>
      </div>
    );
  }

  if (organisationId === undefined) {
    return (
      <div style={{ marginBottom: theme.spacing[16] }}>
        <SmartChipSection>
          <VStack align="stretch" spacing={4} width="100%">
            <Text color="gray.700">
              Lab workbook id is required in the URL (e.g. /lab/00000000-0000-0000-0000-000000000000/sample-spec).
            </Text>
          </VStack>
        </SmartChipSection>
      </div>
    );
  }

  if (mode === 'samples' && isInitialLoading) {
    return <LoadingView />;
  }

  const handleOpenGeneMode = () => {
    if (resolvedLabWorkbookId) {
      const next = new URLSearchParams(searchParams);
      next.set('mode', 'genes');
      const suffix = next.toString();
      navigate(`/lab/${resolvedLabWorkbookId}/gene-template${suffix ? `?${suffix}` : ''}`, false);
      return;
    }
    setSearchParamsStable(
      prev => {
        const nextParams = new URLSearchParams(prev);
        nextParams.set('mode', 'genes');
        return nextParams;
      },
      { replace: true },
    );
  };

  // Title is rendered at the Lab page level; no per-view controls here.

  if (mode === 'genes') {
    return (
      <div style={{ marginBottom: theme.spacing[16] }}>
        <SmartChipSection>
          <GeneSelectionSheet
            organisationId={organisationId}
            organisationIdParam={organisationIdParam}
            invalidOrganisationId={invalidOrganisationId}
            isEmbedded
            linkedPanelId={genePanelId ?? null}
            onLinkPanel={handleLinkGenePanel}
            isLinkingPanel={isLinkingGenePanel}
            labWorkbookStatus={labWorkbookStatus}
            allowGeneTemplateEdits={allowGeneTemplateEdits}
          />
        </SmartChipSection>
      </div>
    );
  }

  const shouldShowCompactFooterLabels = actionFooterDensity !== 'full';
  const shouldHideRowSummary = actionFooterDensity === 'compact';
  const badgeMaxWidth = isEditingSubmittedSamples
    ? actionFooterDensity === 'compact'
      ? '100px'
      : actionFooterDensity === 'condensed'
        ? '140px'
        : '180px'
    : actionFooterDensity === 'compact'
      ? '160px'
      : actionFooterDensity === 'condensed'
        ? '240px'
        : undefined;

  const sampleActionFooterLeading = (
    <Flex
      align="center"
      width="100%"
      minWidth={0}
      columnGap={shouldShowCompactFooterLabels ? theme.spacing[3] : theme.spacing[4]}
      justify="space-between"
    >
      <HStack
        spacing={shouldShowCompactFooterLabels ? theme.spacing[2] : theme.spacing[3]}
        align="center"
        minWidth={0}
        flex="1"
      >
        {submittedSampleRowCount > 0 ? (
          <ResistomapSwitch
            options={
              [
                { value: 'draft', label: 'Draft' },
                { value: 'submitted', label: 'Submitted' },
              ] as const
            }
            value={adminRowViewMode}
            onChange={handleRowViewModeChangeStable}
            isDisabled={isEnteringSubmittedEditMode || isCancellingSubmittedEdits || isResubmittingSubmittedEdits}
            widths={[40, 70]}
          />
        ) : null}
        {canConfigureFirstSampleNumber ? (
          <Tooltip
            label="Applied only before the first submission. Draft sample numbers start from this value."
            placement="top"
            openDelay={200}
            hasArrow
          >
            <HStack spacing={2} align="center" flexShrink={0}>
              <Text fontSize="xs" color="gray.600" whiteSpace="nowrap">
                First sample
              </Text>
              <NumberInput
                size="xs"
                min={1}
                clampValueOnBlur={false}
                keepWithinRange={false}
                maxW="96px"
                value={firstSampleNumberInput}
                onChange={valueString => {
                  setFirstSampleNumberInput(valueString);
                }}
                isDisabled={updateLabWorkbookMetadata.isPending}
              >
                <NumberInputField
                  aria-label="First sample number"
                  borderColor={firstSampleNumberError ? 'red.300' : undefined}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void handleSaveFirstSampleNumber();
                    }
                  }}
                />
              </NumberInput>
              <Button
                size="xs"
                colorScheme="blue"
                variant="outline"
                onClick={() => {
                  void handleSaveFirstSampleNumber();
                }}
                isDisabled={!isFirstSampleNumberDirty || firstSampleNumberError != null}
                isLoading={updateLabWorkbookMetadata.isPending}
              >
                Save
              </Button>
            </HStack>
          </Tooltip>
        ) : null}
        {shouldHideRowSummary || !shouldShowRowSummary ? null : (
          <>
            {submittedSampleRowCount > 0 ? (
              <Text fontSize="xs" color="gray.600" ml={0.5}>
                |
              </Text>
            ) : null}
            <Box minWidth={0}>
              <Text
                fontSize="sm"
                color="gray.600"
                textAlign="right"
                userSelect="none"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                title={rowSummaryTitle}
              >
                {displayedRowSummary}
                {hasRowSummarySegments && !isFiltered ? ' total' : ''}
                {hasRowSummarySegments ? (
                  <>
                    {' ('}
                    {rowSummarySegments.map((segment, index) => (
                      <Fragment key={segment.key}>
                        {segment.onClick ? (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={segment.onClick}
                            onKeyDown={event => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                event.stopPropagation();
                                segment.onClick?.();
                              }
                            }}
                            style={{ cursor: 'pointer', textDecoration: 'underline', userSelect: 'none' }}
                          >
                            {segment.label}
                          </span>
                        ) : (
                          <span style={{ userSelect: 'none' }}>{segment.label}</span>
                        )}
                        {index < rowSummarySegments.length - 1 ? ', ' : ''}
                      </Fragment>
                    ))}
                    {')'}
                  </>
                ) : null}
              </Text>
            </Box>
          </>
        )}
      </HStack>
      {isEditingSubmittedSamples ? null : (
        <Badge
          onClick={handleOpenGeneMode}
          colorScheme={linkedGenePanelColorScheme}
          cursor="pointer"
          userSelect="none"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          maxW={badgeMaxWidth}
          flexShrink={0}
          title={linkedGenePanelDisplayedLabel}
          ml={shouldShowCompactFooterLabels ? theme.spacing[2] : theme.spacing[3]}
        >
          {linkedGenePanelDisplayedLabel}
        </Badge>
      )}
    </Flex>
  );

  const verboseSubmitButtonLabel = selectedReadyToSubmitCount
    ? `Submit ${selectedReadyToSubmitCount === 1 ? 'selected sample' : `${selectedReadyToSubmitCount} selected samples`}`
    : 'Select samples to submit';
  const submitButtonLabel = actionFooterDensity === 'full' ? verboseSubmitButtonLabel : 'Submit';
  const resubmitButtonLabel = 'Submit edits';
  const submitConfirmationOptionLabel =
    pendingSubmitConfirmation?.kind === 'numbering'
      ? `Confirm ${formatConfirmedSampleNumbers(pendingSubmitConfirmation)} and submit`
      : selectedReadyToSubmitCount === 1
        ? 'Submit selected sample'
        : `Submit ${selectedReadyToSubmitCount} selected samples`;
  const showSelectAllRowsLink = selectedReadyToSubmitCount === 0 && readyToSubmitCount > 0;
  const handleSelectAllRowsClick = () => {
    handleReadyToSubmitSummaryClick();
  };

  const syncTextWidth = actionFooterDensity === 'full' ? '200px' : '140px';
  const submitButtonWidth = actionFooterDensity === 'full' ? '200px' : '140px';
  const submitMessageMaxWidth = actionFooterDensity === 'full' ? '280px' : '220px';
  const selectAllRowsLinkWidth = actionFooterDensity === 'full' ? '140px' : '120px';

  const sampleActionFooterTrailing = isEditingSubmittedSamples ? (
    <HStack
      spacing={shouldShowCompactFooterLabels ? theme.spacing[2] : theme.spacing[3]}
      align="center"
      justify="flex-end"
      minWidth={0}
      ml={theme.spacing[4]}
    >
      <VStack spacing={1} align="flex-end" maxW={submitMessageMaxWidth} width="100%">
        {missingGenePanel ? (
          <Text fontSize="sm" color="orange.600" textAlign="right">
            {GENE_SELECTION_REQUIRED_MESSAGE}
          </Text>
        ) : null}
        <HStack spacing={shouldShowCompactFooterLabels ? theme.spacing[2] : theme.spacing[3]} align="center">
          {hasBlockingSubmittedEdits ? (
            <Tooltip
              label={pendingEditCount > 0 ? 'Wait for pending edits to sync before cancelling.' : ''}
              isDisabled={pendingEditCount === 0}
              placement="top"
              openDelay={200}
              hasArrow
              shouldWrapChildren
            >
              <Button
                variant="outline"
                width={submitButtonWidth}
                isDisabled={isResubmittingSubmittedEdits || pendingEditCount > 0}
                isLoading={isCancellingSubmittedEdits}
                onClick={handleCancelSubmittedEdits}
                whiteSpace="nowrap"
              >
                Cancel
              </Button>
            </Tooltip>
          ) : null}
          <Tooltip
            label={resubmitButtonTooltip ?? ''}
            isDisabled={resubmitButtonTooltip == null}
            placement="top"
            openDelay={200}
            hasArrow
            shouldWrapChildren
          >
            <Button
              minWidth={submitButtonWidth}
              colorScheme="green"
              isDisabled={
                disableEditing || !canResubmitSampleEdits || !hasDirtySubmittedEdits || isCancellingSubmittedEdits
              }
              isLoading={isResubmittingSubmittedEdits}
              onClick={handleResubmitSubmittedEdits}
              whiteSpace="nowrap"
            >
              {resubmitButtonLabel}
            </Button>
          </Tooltip>
        </HStack>
      </VStack>
    </HStack>
  ) : (
    <HStack
      spacing={shouldShowCompactFooterLabels ? theme.spacing[2] : theme.spacing[3]}
      align="center"
      justify="flex-end"
      minWidth={0}
    >
      <Box width={selectAllRowsLinkWidth} display="flex" justifyContent="flex-end" flexShrink={0}>
        {showSelectAllRowsLink ? (
          <Button
            variant="link"
            size="sm"
            colorScheme="blue"
            onClick={handleSelectAllRowsClick}
            isDisabled={isSubmitting}
            whiteSpace="nowrap"
          >
            Select All Rows
          </Button>
        ) : (
          <Button
            variant="link"
            size="sm"
            colorScheme="blue"
            whiteSpace="nowrap"
            pointerEvents="none"
            aria-hidden="true"
            tabIndex={-1}
            visibility="hidden"
          >
            Select All Rows
          </Button>
        )}
      </Box>
      <VStack spacing={1} align="flex-end" maxW={submitMessageMaxWidth} width="100%">
        {missingGenePanel ? (
          <Text fontSize="sm" color="orange.600" textAlign="right">
            {GENE_SELECTION_REQUIRED_MESSAGE}
          </Text>
        ) : null}
        <Tooltip
          label={submitButtonTooltip ?? ''}
          isDisabled={submitButtonTooltip == null}
          placement="top"
          openDelay={200}
          hasArrow
          shouldWrapChildren
        >
          <Button
            width={submitButtonWidth}
            colorScheme="green"
            isDisabled={disableEditing || !canSubmit}
            isLoading={isSubmitting}
            onClick={handleSubmitClick}
            whiteSpace="nowrap"
          >
            {submitButtonLabel}
          </Button>
        </Tooltip>
      </VStack>
    </HStack>
  );

  return (
    <div style={{ marginBottom: theme.spacing[16] }}>
      <SmartChipSection>
        <VStack align="stretch" spacing={4} width="100%">
          {samplingPointChoicesErrorMessage ? <Text color="red.600">{samplingPointChoicesErrorMessage}</Text> : null}
          <Flex align="center" justify="space-between" wrap="wrap" gap={theme.spacing[4]}>
            <Text color="gray.600" maxW="960px">
              Paste or type sample specification below. The first, blue columns pertain to <i>sampling points</i>. When
              selected, they are colored to highlight other samples collected from the same <i>point</i>, e.g. on
              different days. The yellow columns hold information specific to <i>individual samples</i>. After
              specifying all required information, select and submit rows for laboratory analysis.
            </Text>
          </Flex>
          <Box width="100%">
            <Flex justify="flex-end">
              <Box width={syncTextWidth} flexShrink={0}>
                <Text
                  fontSize="xs"
                  color={syncIndicatorColor}
                  textAlign="right"
                  noOfLines={1}
                  title={syncIndicatorText}
                  overflow="hidden"
                >
                  {syncIndicatorText}
                </Text>
              </Box>
            </Flex>
            <SmartChipActionFooter leading={sampleActionFooterLeading} trailing={sampleActionFooterTrailing} />
          </Box>
          <Box width="100%">
            <Box borderRadius={8} border="1px solid" borderColor="gray.200" overflowX="auto" width="100%">
              <div
                ref={gridContainerRef}
                style={{ width: '100%', height: gridHeight, minHeight: GRID_DEFAULT_MIN_HEIGHT }}
                className={gridThemeClass}
              >
                <AgGridReact<SampleSheetRowDraft>
                  ref={gridRef}
                  theme={themeQuartz}
                  domLayout={gridDomLayout}
                  rowData={visibleRows}
                  columnDefs={columnDefs}
                  context={gridContext}
                  defaultColDef={defaultColDef}
                  rowHeight={25}
                  rowGroupPanelShow="onlyWhenGrouping"
                  cellSelection={cellSelection}
                  onCellClicked={onCellClicked}
                  onCellDoubleClicked={onCellDoubleClicked}
                  allowContextMenuWithControlKey
                  rowSelection={rowSelection}
                  onCellEditingStarted={onCellEditingStarted}
                  onCellEditingStopped={onCellEditingStopped}
                  headerHeight={64}
                  animateRows
                  getRowId={(rowParams: { data: SampleSheetRowDraft }) => rowParams.data.rowId}
                  onCellValueChanged={onCellValueChanged}
                  onCellKeyDown={handleCellKeyDown}
                  onCellSelectionChanged={handleCellSelectionChanged}
                  onFilterChanged={handleFilterChanged}
                  onBodyScroll={handleBodyScroll}
                  onFirstDataRendered={handleFirstDataRendered}
                  onModelUpdated={handleModelUpdated}
                  onGridSizeChanged={handleGridSizeChanged}
                  getContextMenuItems={getContextMenuItems}
                  processDataFromClipboard={processDataFromClipboard}
                  processCellForClipboard={processCellForClipboard}
                  processCellFromClipboard={processCellFromClipboard}
                  onPasteStart={onPasteStart}
                  onPasteEnd={onPasteEnd}
                  onFillStart={handleFillStart}
                  onFillEnd={handleFillEnd}
                  suppressCutToClipboard
                  getRowClass={getRowClass}
                  tooltipShowDelay={200}
                  tooltipHideDelay={TOOLTIP_HIDE_DELAY_MS}
                />
              </div>
            </Box>
            {floatingHorizontalScrollState.visible ? (
              <div
                ref={floatingHorizontalScrollRef}
                className="sample-sheet-floating-horizontal-scroll"
                style={{ left: floatingHorizontalScrollState.left, width: floatingHorizontalScrollState.width }}
                onScroll={handleFloatingHorizontalScroll}
                role="region"
                aria-label="Sample sheet horizontal scroll"
                tabIndex={0}
              >
                <div
                  className="sample-sheet-floating-horizontal-scroll__spacer"
                  style={{ width: floatingHorizontalScrollState.contentWidth }}
                />
              </div>
            ) : null}
            {adminRowViewMode === 'submitted' ? null : (
              <HStack spacing={theme.spacing[2]} align="center" mt={theme.spacing[4]}>
                <NumberInput
                  size="sm"
                  min={1}
                  max={500}
                  value={rowsToAddCount}
                  isDisabled={disableEditing}
                  onChange={(_, valueAsNumber) => {
                    setRowsToAddCount(Number.isFinite(valueAsNumber) ? Math.max(1, valueAsNumber) : 1);
                  }}
                  width="80px"
                >
                  <NumberInputField
                    aria-label="Number of rows to add"
                    paddingInline={theme.spacing[2]}
                    textAlign="center"
                  />
                </NumberInput>
                <Button onClick={handleAddRows} colorScheme="blue" isDisabled={disableEditing}>
                  Add rows
                </Button>
              </HStack>
            )}
          </Box>
        </VStack>
      </SmartChipSection>
      <MultiOptionConfirmModal
        isOpen={blockedSamplingPointEdit != null}
        onClose={() => {
          setBlockedSamplingPointEdit(null);
          pendingSamplingPointEditTargetRef.current = null;
        }}
        title="Sampling point is shared"
        message={
          blockedSamplingPointEdit ? (
            <VStack align="start" spacing={3}>
              <Text>{formatSamplingPointUsageSentence(blockedSamplingPointEdit.usage)}</Text>
              <Text>
                <b>To change the sampling point for only this sample</b>, clear the sampling point information for this
                row before entrering new information.
              </Text>
              <Text>
                <b>To edit the sampling point for all samples</b>,<br />
                cancel, submit your other edits, and only then edit the sampling point on its admin page.
              </Text>
            </VStack>
          ) : null
        }
        options={[
          {
            key: 'clear',
            label: 'Clear sampling point for this row',
            description: '',
            colorScheme: 'orange',
            variant: 'solid',
            onSelect: () => {
              const target = pendingSamplingPointEditTargetRef.current;
              const rowId = blockedSamplingPointEdit?.rowId;
              if (!rowId || !target || target.rowId !== rowId) {
                return;
              }
              const didClear = clearSubmittedSamplingPointForRow(rowId);
              if (!didClear) {
                return;
              }
              const api = gridRef.current?.api ?? null;
              if (!api) {
                return;
              }
              const node = api.getRowNode(rowId);
              const rowIndex = node?.rowIndex ?? null;
              if (rowIndex == null) {
                return;
              }
              api.startEditingCell({ rowIndex, colKey: target.colId, eventKey: target.eventKey });
            },
          },
        ]}
      />
      <MultiOptionConfirmModal
        isOpen={pendingSubmitConfirmation != null}
        onClose={() => {
          setPendingSubmitConfirmation(null);
        }}
        title={pendingSubmitConfirmation?.kind === 'numbering' ? 'Confirm sample numbering' : 'Confirm sample submission'}
        message={
          pendingSubmitConfirmation?.kind === 'numbering' ? (
            <VStack align="start" spacing={3}>
              <Text>
                You are about to submit{' '}
                <b>
                  {pendingSubmitConfirmation.count === 1
                    ? `sample number ${pendingSubmitConfirmation.first}`
                    : `${pendingSubmitConfirmation.count} samples numbered ${formatConfirmedSampleNumbers(pendingSubmitConfirmation)}`}
                </b>
                .
              </Text>
              <Text>
                Confirm that this is the intended numbering. It cannot be changed from this workbook after submission.
              </Text>
              <Text>{getSampleNumberingContinuationMessage(sampleNumberingContinuationState)}</Text>
            </VStack>
          ) : (
            'I assure the submitted samples are filled in correctly and are in the order I wish to have them analysed.'
          )
        }
        options={[
          {
            key: 'confirm-submit',
            label: submitConfirmationOptionLabel,
            colorScheme: 'green',
            variant: 'solid',
            onSelect: async () => {
              await handleSubmit();
            },
          },
        ]}
      />
      <MultiOptionConfirmModal
        isOpen={pendingMetadataLimitationsConfirmation != null}
        onClose={() => {
          setPendingMetadataLimitationsConfirmation(null);
          setMetadataLimitationsAcknowledged(false);
        }}
        title="Confirm sample limitations"
        message={
          pendingMetadataLimitationsConfirmation ? (
            <VStack align="stretch" spacing={4}>
              <Text>
                {pendingMetadataLimitationsConfirmation.payload.affectedRowCount === 1
                  ? '1 selected sample is missing some metadata. You can still submit it, with the following limitations.'
                  : `${pendingMetadataLimitationsConfirmation.payload.affectedRowCount} selected samples are missing some metadata. You can still submit them, with the following limitations.`}
              </Text>
              {metadataLimitationGroups.map(group => (
                <Box key={group.category} borderWidth="1px" borderColor="gray.200" borderRadius="sm" px={3} py={2}>
                  <VStack align="stretch" spacing={1} mb={2}>
                    {group.fields.map(summary => (
                      <Text key={summary.field} fontSize="sm">
                        <b>{metadataLimitationFieldLabels[summary.field]}:</b> {summary.sampleText}
                      </Text>
                    ))}
                  </VStack>
                  <Text>{getMetadataLimitationConsequence(group)}</Text>
                  {group.category === 'volume_normalisation' &&
                  pendingMetadataLimitationsConfirmation.payload.partialVolumeNormalisation ? (
                    <Text fontWeight="semibold" mt={2}>
                      {MIXED_FILTERED_VOLUME_MESSAGE}
                    </Text>
                  ) : null}
                </Box>
              ))}
              {pendingMetadataLimitationsConfirmation.payload.partialVolumeNormalisation &&
              !metadataLimitationGroups.some(group => group.category === 'volume_normalisation') ? (
                <Text fontWeight="semibold">{MIXED_FILTERED_VOLUME_MESSAGE}</Text>
              ) : null}
              <Checkbox
                isChecked={metadataLimitationsAcknowledged}
                onChange={event => {
                  setMetadataLimitationsAcknowledged(event.target.checked);
                }}
              >
                I understand these limitations and approve analysing these samples as they are.
              </Checkbox>
            </VStack>
          ) : null
        }
        options={[
          {
            key: 'proceed-metadata-limitations',
            label:
              pendingMetadataLimitationsConfirmation?.source === 'resubmit'
                ? 'Accept limitations and submit edits'
                : 'Accept limitations and submit',
            colorScheme: 'orange',
            variant: 'solid',
            isDisabled: !metadataLimitationsAcknowledged,
            onSelect: async () => {
              const outcome = await handleProceedWithMetadataLimitations();
              return outcome === 'metadata_limitations_confirmation' ? KEEP_CONFIRMATION_MODAL_OPEN : undefined;
            },
          },
        ]}
      />
      <MultiOptionConfirmModal
        isOpen={pendingPartialVolumeConfirmation != null}
        onClose={() => {
          setPendingPartialVolumeConfirmation(null);
        }}
        title="Proceed with partial volume normalisation?"
        message={
          pendingPartialVolumeConfirmation ? (
            <VStack align="start" spacing={3}>
              <Text>
                {pendingPartialVolumeConfirmation.payload.withVolumeCount} water sample
                {pendingPartialVolumeConfirmation.payload.withVolumeCount === 1 ? ' has' : 's have'} a filtered volume
                and {pendingPartialVolumeConfirmation.payload.withoutVolumeCount}{' '}
                {pendingPartialVolumeConfirmation.payload.withoutVolumeCount === 1 ? 'does' : 'do'} not.
              </Text>
              <Text fontWeight="semibold">{MIXED_FILTERED_VOLUME_MESSAGE}</Text>
            </VStack>
          ) : null
        }
        options={[
          {
            key: 'proceed-partial-volume',
            label:
              pendingPartialVolumeConfirmation?.source === 'resubmit'
                ? 'Proceed and submit edits'
                : 'Proceed and submit',
            colorScheme: 'orange',
            variant: 'solid',
            onSelect: async () => {
              await handleProceedWithPartialVolumeConfirmation();
            },
          },
        ]}
      />
    </div>
  );
}

const SmartChipSection = styled(MaxWidth)`
  width: 100%;
  max-width: none;
  ${sidePadding}
  padding-top: ${theme.spacing[4]};
  padding-bottom: ${theme.spacing[4]};
`;
